window.__NYELETI_BUILD='B1';
/* Nyeleti Sports — behaviour for the ported markup.

   The site was built with React and GSAP. What ships here is the rendered
   result of that build, so the structure and class names are identical but
   nothing is wired up. This supplies the same behaviour without the framework:
   the hero slider, the announcement rotation, the logo marquee, the scroll
   reveals, the menus, the overlays and the draggable rails.

   Everything degrades to "visible and static" when scripting or motion is off.
   No element is left hidden waiting for an animation that never runs. */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  };

  /* ── Load curtain / page change ─────────────────────────────────────────
     The built site plays a preloader on first load and a transition between
     routes. On Shopify a page change is a real navigation, so the same curtain
     does both jobs: it lifts once this page has painted, and it drops back down
     when a link to another page on this store is followed, so the browser's
     blank gap is covered instead of flashing. */
  (function preloader() {
    var el = $('.preloader');
    if (!el) return;

    // Once per visit, not once per page. The build plays it when the site is
    // opened; every navigation after that gets the route transition instead.
    var SEEN = 'nyeleti:preloaded';
    var seen = false;

    /* sessionStorage is the natural home for a once-per-visit flag, but it is
       not always writable — a browser with site data blocked, private mode in
       older Safari, and the theme editor's cross-origin preview frame all
       throw or hand back a storage the page cannot persist to. When that
       happened the flag never stuck, so the curtain counted 00 -> 100 again on
       the very next page: one navigation, two full counts. A session cookie
       carries the same flag through those cases, and either source is enough
       to say the visit has already seen it. */
    function cookieSeen() {
      return document.cookie.indexOf(SEEN + '=1') >= 0;
    }
    try { seen = sessionStorage.getItem(SEEN) === '1'; } catch (err) {}
    if (!seen) seen = cookieSeen();
    /* An in-flight run marks the element itself, so a second execution of this
       file — a duplicated script tag, a back/forward restore — cannot start a
       second count over the first. */
    if (seen || el.getAttribute('data-preloader-ran') === '1') {
      el.classList.add('preloader--done');
      return;
    }
    el.setAttribute('data-preloader-ran', '1');
    try { sessionStorage.setItem(SEEN, '1'); } catch (err) {}
    try { document.cookie = SEEN + '=1; path=/; SameSite=Lax'; } catch (err) {}

    // The percentage counter. It sat at "00%" because nothing ever advanced it.
    var counter = null;
    $$('.preloader span').some(function (s) {
      if (/^\s*\d{1,3}%\s*$/.test(s.textContent || '')) { counter = s; return true; }
      return false;
    });

    function pad(n) { return (n < 10 ? '0' : '') + n; }

    /* Timing. The count used to start only once `load` fired, which tied the
       whole curtain to the cache: a warm load began counting straight away and
       was gone in ~2s, a cold one sat on "00%" for the full 2.6s fallback and
       then still took 1.7s to count — so the same screen ran anywhere between
       2s and 4.6s. Now the count starts immediately and always plays in full,
       and the curtain waits for whichever finishes last, capped. That puts
       every visit between ~1.8s and ~2.9s. */
    var COUNT_MS = 3400;   // how long 00 -> 100 takes
    var HOLD_MS = 520;     // beat on "100%" before the curtain rises
    var MAX_WAIT = 4200;   // a stalled asset must never hold the curtain down

    var counted = false;
    var loaded = document.readyState === 'complete';
    var lifted = false;

    function lift() {
      if (lifted || !counted || !loaded) return;
      lifted = true;
      window.setTimeout(function () { el.classList.add('preloader--done'); }, HOLD_MS);
    }

    function markLoaded() { loaded = true; lift(); }
    if (!loaded) window.addEventListener('load', markLoaded);
    window.setTimeout(markLoaded, MAX_WAIT);

    /* Absolute failsafe. The count runs on requestAnimationFrame, and a
       backgrounded or throttled tab does not tick it at all — the counter then
       never reaches 100 and the curtain would sit over the whole site until
       the tab was focused. Lift regardless once the longest honest run is up. */
    window.setTimeout(function () {
      counted = true;
      loaded = true;
      if (counter) counter.textContent = '100%';
      lift();
    }, MAX_WAIT + COUNT_MS);

    (function run() {
      if (!counter || reduceMotion) { counted = true; lift(); return; }
      var started = null;
      (function frame(now) {
        if (started === null) started = now;
        var t = Math.min(1, (now - started) / COUNT_MS);
        /* Strictly linear. Any easing makes the number speed up or slow down
           mid-count, which reads as stutter rather than as motion — the digits
           are the animation here, so an even rate is the whole point. Jitter
           also came from the glyphs themselves: proportional digits change
           width as they cycle, so the text nudged sideways on nearly every
           frame. The counter is set in tabular figures in CSS to hold it
           still. */
        counter.textContent = pad(Math.round(t * 100)) + '%';
        if (t < 1) window.requestAnimationFrame(frame);
        else { counter.textContent = '100%'; counted = true; lift(); }
      })(performance.now());
    })();
  })();

  /* ── Route transition ───────────────────────────────────────────────────
     Opening another page wipes two panels in and names the destination. The
     name comes from the link that was clicked — that is the product or page
     title the visitor just pressed — rather than the URL slug, which printed
     things like "LIFESTYLE 2". */
  (function routeTransition() {
    var root = $('[data-route-transition]');
    if (!root || reduceMotion) return;

    var label = $('[data-route-label]', root);
    var panels = $$('[data-route-panel]', root);
    var stitches = $('[data-route-stitches]', root);

    var STATIC = {
      '/': 'Home',
      '/collections/all': 'Shop All',
      '/collections': 'Shop by Collection',
      '/cart': 'Cart',
      '/search': 'Search',
      '/pages/custom-designs': 'Custom Designs',
      '/pages/custom-order': 'Custom Order',
      '/pages/our-partners': 'Our Partners',
      '/pages/our-story': 'About Us',
      '/pages/about-us': 'About Us',
      '/pages/contact': 'Contact Us',
      '/pages/sponsorships': 'Sponsorship',
      '/pages/resellers': 'Resellers',
      '/pages/crawford-international': 'Crawford International',
      '/pages/city-of-tshwane': 'City of Tshwane',
      '/pages/maragon-schools': 'Maragon Schools',
      '/crawford': 'Crawford International',
      '/cityoftshwane': 'City of Tshwane',
      '/maragon': 'Maragon Schools'
    };

    function titleCase(slug) {
      return slug.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
    }

    function nameFor(url, link) {
      var path = url.pathname.replace(/\/+$/, '') || '/';
      // The all-products listing doubles as New Arrivals when sorted newest
      // first — clicking New Arrivals must not announce "Shop All".
      if (path === '/collections/all' && /created-descending/.test(url.search)) return 'New Arrivals';
      if (STATIC[path]) return STATIC[path];
      // The link's own text is the real title — the card says "The Made2Fly
      // Varsity Jacket" where the slug only says "lifestyle-2".
      // A nav link holds two spans — the name and its description — with no
      // whitespace between them, so textContent read "Maragon SchoolsSchools
      // Sports Uniforms". Take the first element's text when there is one.
      // Partner cards are an <img>, a category label and an <h3> in one
      // link, so textContent mashes them: "Athletics ClubCity of Tshwane
      // Athletics Club". The heading is the name — prefer it outright.
      var headline = link.querySelector('h1, h2, h3, h4');
      var first = link.firstElementChild;
      var text = (link.getAttribute('aria-label') ||
                  (headline && headline.textContent) ||
                  (first && first.textContent) ||
                  link.textContent || '').replace(/\s+/g, ' ').trim();
      // Belt and braces for anything still concatenated: fold a word glued to
      // itself ("SchoolsSchools") and immediate word repeats ("School School").
      text = text.replace(/([A-Za-z]{3,})\1/g, '$1');
      text = text.replace(/\b([A-Za-z]{3,})( \1\b)+/gi, '$1');
      // The collection deck's jump links start with their index, so the label
      // read "02 Mzansi Supporter Jerseys". Strip that index — but only when it
      // is unmistakably an index, meaning zero-padded or followed by a dash.
      // Matching any leading digit ate the start of real product names:
      // "3/4 Leggings" announced as "/4 Leggings", "5 Inch Shorts" as
      // "Inch Shorts".
      text = text.replace(/^\s*(?:\d{1,2}\s*[—–-]\s*|0\d\s+)/, '');
      if (text && text.length < 60 && !/^(shop|view|buy|add)\b/i.test(text)) return text;
      var slug = path.split('/').filter(Boolean).pop() || '';
      return slug ? titleCase(slug) : 'Nyeleti Sports';
    }

    function clear() { root.classList.remove('route-transition--active'); }
    window.addEventListener('pageshow', function (e) { if (e.persisted) clear(); });

    document.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('a[href]');
      if (!link || e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (link.target && link.target !== '_self') return;
      if (link.hasAttribute('download')) return;
      var raw = link.getAttribute('href') || '';
      if (/^(mailto|tel|#)/i.test(raw)) return;

      var url;
      try { url = new URL(link.href, location.href); } catch (err) { return; }
      if (url.origin !== location.origin) return;
      if (url.pathname === location.pathname && url.search === location.search) return;

      e.preventDefault();
      if (label) label.textContent = nameFor(url, link);
      root.classList.add('route-transition--active');

      panels.forEach(function (panel, index) {
        panel.animate(
          [
            { transform: 'translate3d(' + (index % 2 ? '-108%' : '108%') + ',0,0) skewX(-4deg)' },
            { transform: 'translate3d(0,0,0) skewX(-4deg)' }
          ],
          { duration: 450, delay: index * 36, easing: 'cubic-bezier(.76,0,.24,1)', fill: 'forwards' }
        );
      });
      if (stitches) {
        stitches.animate(
          [{ opacity: 0, transform: 'scaleX(.1)' }, { opacity: 0.9, transform: 'scaleX(1)' }],
          { duration: 390, delay: 90, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards' }
        );
      }
      window.setTimeout(function () { location.assign(url.href); }, 510);
    }, true);
  })();

  /* ── Announcement strip ─────────────────────────────────────────────────
     Three messages stacked on top of each other; show one at a time. */
  (function announcements() {
    var items = $$('.announcement-item');
    if (!items.length) return;

    /* On a phone the strip is too narrow for the longer messages, which
       wrapped to two cramped lines. Replace the rotation with a single
       continuously sliding line carrying every message. */
    if (window.matchMedia('(max-width: 767px)').matches) {
      var bar = items[0].parentNode;
      // Read the message span only. Each item already carries its own gold
      // stars either side, so taking the item's whole textContent pulled those
      // in as plain characters \u2014 then joining with another star put three in a
      // row and stripped the gold, because it all became one uncoloured string.
      var texts = items.map(function (el) {
        var body = el.querySelector('span:not([aria-hidden])') || el;
        return body.textContent.replace(/[\u2726\u2727]/g, ' ').replace(/\s+/g, ' ').trim();
      }).filter(Boolean);
      items.forEach(function (el) { el.style.display = 'none'; });

      var tick = document.createElement('div');
      // Same type treatment the strip uses for its static messages, or the
      // ticker renders at the browser default and looks nothing like the bar.
      tick.className = 'announce-ticker announcement-ticker absolute inset-0 flex items-center';
      tick.setAttribute('aria-hidden', 'true');
      var track = document.createElement('span');
      track.className = 'type-spec text-[10px] tracking-[0.14em] whitespace-nowrap';

      // Built as nodes, not one string, so the separator keeps the brand gold.
      function star() {
        var s = document.createElement('span');
        s.className = 'announce-star text-gold';
        s.textContent = '\u2726';
        return s;
      }
      function word(t) {
        var s = document.createElement('span');
        s.textContent = t;
        return s;
      }
      for (var pass = 0; pass < 2; pass++) {          // twice, so the loop never gaps
        texts.forEach(function (t) {
          track.appendChild(star());
          track.appendChild(word(t));
        });
      }
      tick.appendChild(track);
      bar.appendChild(tick);

      // The strip is still built under reduced motion — it just holds still,
      // rather than falling back to the stacked messages it cannot fit.
      if (!reduceMotion) {
        var x = 0;
        (function slide() {
          var half = track.scrollWidth / 2;
          if (half > 0) {
            x += 0.55;
            if (x >= half) x -= half;
            track.style.transform = 'translate3d(' + (-x) + 'px,0,0)';
          }
          window.requestAnimationFrame(slide);
        })();
      }
      return;
    }

    if (items.length < 2) return;
    var index = 0;
    items.forEach(function (item, i) { item.classList.toggle('is-active', i === 0); });
    if (reduceMotion) return;
    window.setInterval(function () {
      index = (index + 1) % items.length;
      items.forEach(function (item, i) { item.classList.toggle('is-active', i === index); });
    }, 3800);
  })();

  /* ── Hero slider ────────────────────────────────────────────────────────
     Media layers and copy blocks are indexed in pairs. */
  (function hero() {
    var media = $$('[data-hero-media]');
    var copy = $$('[data-hero-copy]');
    if (media.length < 2) return;

    var index = 0;
    var timer = null;

    var slices = $$('[data-hero-slice]');
    var locked = false;
    var settle = null;

    function paint(i) {
      media.forEach(function (el, n) {
        el.classList.toggle('is-active', n === i);
        el.style.opacity = n === i ? '1' : '0';
        el.style.clipPath = n === i ? 'inset(0 0 0 0)' : 'inset(0 0 0 100%)';
        el.style.transform = 'none';
      });
      copy.forEach(function (el, n) {
        el.classList.toggle('is-active', n === i);
        el.style.opacity = n === i ? '1' : '0';
        el.style.pointerEvents = n === i ? 'auto' : 'none';
        // Clear the blur and offset the transition left behind, or the previous
        // headline lingers as a smear behind the new one.
        el.style.filter = 'none';
        el.style.transform = 'none';
      });
    }

    /* The build's transition, ported: the angled bars sweep in, the outgoing
       slide is wiped away, the incoming one is wiped on from the other side and
       its copy rises out of a blur, then the bars sweep off. Without this the
       slide change was a plain crossfade. */
    function show(next, direction) {
      var to = (next + media.length) % media.length;
      if (to === index) return;
      var from = index;
      index = to;

      if (reduceMotion || !slices.length) { paint(index); return; }
      if (locked) { paint(index); return; }
      locked = true;
      if (settle) { window.clearTimeout(settle); settle = null; }

      var dir = direction || 1;
      var EASE = 'cubic-bezier(.76,0,.24,1)';

      // The bars are driven with the Web Animations API, like the slides
      // themselves. The old approach toggled a class and relied on the CSS
      // animation restarting on re-add — on the mobile build the restart
      // never happened, so phones got the slide change with no wipe.
      slices.forEach(function (el, i) {
        if (el.getAnimations) el.getAnimations().forEach(function (a) { a.cancel(); });
        var fromX = i % 2 ? '125%' : '-125%';
        var toX = i % 2 ? '-125%' : '125%';
        el.animate(
          [
            { opacity: 0, transform: 'translateX(' + fromX + ')', offset: 0 },
            { opacity: 0.94, transform: 'translateX(0)', offset: 0.22 },
            { opacity: 0.94, transform: 'translateX(0)', offset: 0.62 },
            { opacity: 0, transform: 'translateX(' + toX + ')', offset: 1 }
          ],
          { duration: 620, delay: i * 26, easing: 'cubic-bezier(.76,0,.24,1)', fill: 'forwards' }
        );
      });

      // A finished `fill: forwards` animation keeps overriding inline styles for
      // good. Leaving them stacked meant every slide kept an old "fade to 0"
      // applied, so after one cycle the whole hero was invisible. Clear each
      // element's animations before giving it a new one.
      media.concat(copy).forEach(function (el) {
        if (el.getAnimations) el.getAnimations().forEach(function (a) { a.cancel(); });
      });

      var out = media[from], into = media[index];
      out.animate(
        [
          { opacity: 1, transform: 'scale(1) translateX(0)', clipPath: 'inset(0 0 0 0)' },
          { opacity: 0, transform: 'scale(1.14) translateX(' + (-8 * dir) + '%)',
            clipPath: dir > 0 ? 'inset(0 100% 0 0)' : 'inset(0 0 0 100%)' }
        ],
        { duration: 340, delay: 30, easing: EASE, fill: 'forwards' }
      );
      into.style.opacity = '1';
      into.animate(
        [
          { transform: 'scale(1.14) translateX(' + (10 * dir) + '%)',
            clipPath: dir > 0 ? 'inset(0 0 0 100%)' : 'inset(0 100% 0 0)' },
          { transform: 'scale(1) translateX(0)', clipPath: 'inset(0 0 0 0)' }
        ],
        { duration: 420, delay: 80, easing: EASE, fill: 'forwards' }
      );

      copy.forEach(function (el, n) {
        if (n === from) {
          el.animate([{ opacity: 1, transform: 'translateY(0)', filter: 'blur(0px)' },
                      { opacity: 0, transform: 'translateY(-34px)', filter: 'blur(12px)' }],
                     { duration: 220, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' });
          el.style.pointerEvents = 'none';
        } else if (n === index) {
          el.classList.add('is-active');
          el.style.opacity = '1';
          el.style.pointerEvents = 'auto';
          el.animate([{ opacity: 0, transform: 'translateY(42px)', filter: 'blur(14px)' },
                      { opacity: 1, transform: 'translateY(0)', filter: 'blur(0px)' }],
                     { duration: 380, delay: 180, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards' });
        }
      });

      // Settle to a plain, animation-free end state so the next transition
      // starts from something predictable.
      settle = window.setTimeout(function () {
        settle = null;
        media.concat(copy).forEach(function (el) {
          if (el.getAnimations) el.getAnimations().forEach(function (a) { a.cancel(); });
        });
        paint(index);
        locked = false;
      }, 560);
    }

    function start() {
      if (reduceMotion) return;
      stop();
      timer = window.setInterval(function () { show(index + 1, 1); }, 6000);
    }
    function stop() { if (timer) { window.clearInterval(timer); timer = null; } }

    paint(0);
    var prev = $('[data-hero-prev]');
    var next = $('[data-hero-next]');
    // Bind on click, not pointerdown: the slide captures the pointer and would
    // otherwise swallow the press before the arrow ever saw it.
    if (prev) prev.addEventListener('click', function () { stop(); show(index - 1, -1); start(); });
    if (next) next.addEventListener('click', function () { stop(); show(index + 1, 1); start(); });

    // Drag anywhere on the hero to change slide — the build lets you throw it
    // with the mouse, so a click-and-drag has to move it, not just the arrows.
    var stage = media[0].closest('section') || media[0].parentNode;
    var startX = null, dragging = false;
    stage.style.touchAction = 'pan-y';

    stage.addEventListener('pointerdown', function (e) {
      if (e.target.closest('a, button')) return;
      startX = e.clientX; dragging = true;
      stop();
    });
    stage.addEventListener('pointermove', function (e) {
      if (!dragging || startX === null) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 6) e.preventDefault();
    });
    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (type) {
      stage.addEventListener(type, function (e) {
        if (!dragging || startX === null) { dragging = false; return; }
        var dx = (e.clientX || 0) - startX;
        startX = null; dragging = false;
        if (Math.abs(dx) >= 45) show(index + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1);
        start();
      });
    });

    // Hover-pause is a mouse behaviour. On touch the tap fires mouseenter,
    // autoplay stops, and mouseleave never arrives — the slider just dies.
    if (window.matchMedia('(hover: hover)').matches) {
      stage.addEventListener('mouseenter', stop);
      stage.addEventListener('mouseleave', start);
    }
    start();
  })();

  /* ── Logo marquee ───────────────────────────────────────────────────────
     The track is the logo strip repeated three times, so shifting by exactly
     one third and wrapping loops seamlessly. */
  (function marquee() {
    $$('.marquee-viewport').forEach(function (viewport) {
      var track = viewport.firstElementChild;
      if (!track) return;
      var offset = 0;
      var paused = false;

      /* The strip drifts at the same pixels per second as the product rails.
         That is what "the same speed as the New Arrivals cards" has to mean:
         the two tracks are quite different lengths, so matching their cycle
         TIME would leave one visibly faster than the other. Measured off a rail
         rather than written down, so the two stay matched at every breakpoint
         and if the card size ever changes. RAIL_SECONDS mirrors `duration` in
         the rails block below — change one, change both. */
      var RAIL_SECONDS = 38;
      var FALLBACK_PPS = 34;
      var pps = FALLBACK_PPS;

      function measureSpeed() {
        var stage = document.querySelector('.serotoninn-products__viewport, [data-product-stage]');
        var t = stage && (stage.querySelector('.serotoninn-products__track') || stage.firstElementChild);
        if (!t) { pps = FALLBACK_PPS; return; }
        var gap = parseFloat(getComputedStyle(t).columnGap) || 0;
        var period = (t.getBoundingClientRect().width + gap) / 2;
        pps = period > 0 ? period / RAIL_SECONDS : FALLBACK_PPS;
      }
      measureSpeed();
      window.addEventListener('resize', measureSpeed);
      /* The rails duplicate their track after this runs, so an early
         measurement can read half the eventual width. Take it again once the
         page has settled. */
      window.setTimeout(measureSpeed, 1200);

      function third() { return track.scrollWidth / 3; }
      function paint() {
        var t = third();
        if (t) offset = ((offset % t) + t) % t;
        track.style.transform = 'translate3d(' + -offset + 'px,0,0)';
      }

      viewport.addEventListener('mouseenter', function () { paused = true; });
      viewport.addEventListener('mouseleave', function () { paused = false; });

      // The strip is draggable on the built site (it carries data-cursor="drag"),
      // so auto-scroll alone is not the same thing — it has to be throwable.
      var startX = null, startOffset = 0;
      viewport.style.touchAction = 'pan-y';
      viewport.style.cursor = 'grab';

      viewport.addEventListener('pointerdown', function (e) {
        if (e.target.closest('a, button')) return;
        startX = e.clientX;
        startOffset = offset;
        paused = true;
        viewport.style.cursor = 'grabbing';
      });
      viewport.addEventListener('pointermove', function (e) {
        if (startX === null) return;
        e.preventDefault();
        offset = startOffset - (e.clientX - startX);
        paint();
      });
      ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (type) {
        viewport.addEventListener(type, function () {
          if (startX === null) return;
          startX = null;
          paused = false;
          viewport.style.cursor = 'grab';
        });
      });

      if (reduceMotion) { paint(); return; }
      /* Driven by elapsed time rather than by the frame, so the strip moves at
         the same rate on a 60Hz and a 120Hz screen — a per-frame step ran at
         double speed on the latter, which is half of why it never matched the
         rails. */
      var lastFrame = 0;
      window.requestAnimationFrame(function step(now) {
        var delta = lastFrame ? Math.min(now - lastFrame, 120) : 16;
        lastFrame = now;
        if (!paused) { offset += pps * (delta / 1000); paint(); }
        window.requestAnimationFrame(step);
      });
    });
  })();

  /* -- Process journey ----------------------------------------------------
     Ported from the built site's `Process` component. The four phases run as
     one continuous GSAP timeline: blueprint strokes draw themselves on, the
     spinners and flow dashes turn, the rail fills and the pulse walks it, and
     the orbits and scan line sweep across the whole sequence. The timer-driven
     crossfade that stood in for it before only faded the copy, which is why it
     never matched. It survives below as the reduced-motion / no-GSAP path. */
  (function process() {
    var steps = $$('[data-process-step]');
    if (steps.length < 2) return;
    var ticks = $$('[data-process-tick]');
    var visuals = $$('[data-process-visual]');
    var stars = $$('[data-process-constellation]');

    // The generic reveal must not also own these -- two transitions competing
    // for opacity left the stage blank.
    steps.concat(visuals, stars).forEach(function (el) {
      el.classList.remove('will-rise', 'has-risen');
      el.style.removeProperty('transition');
    });

    if (!reduceMotion && window.gsap && journey()) return;

    /* Fallback: plain crossfade, driven by the stylesheet. */
    var index = 0;
    function paint(list, activeOpacity) {
      list.forEach(function (el, i) {
        var on = i === index;
        el.classList.toggle('is-phase', on);
        el.style.opacity = on ? activeOpacity : '0';
        el.style.visibility = 'visible';
      });
    }
    function show(next) {
      index = (next + steps.length) % steps.length;
      paint(steps, '1');
      paint(ticks, '1');
      paint(visuals, '0.95');
      paint(stars, '0.85');
      ticks.forEach(function (el, i) { el.style.opacity = i === index ? '1' : '0.3'; el.style.visibility = 'visible'; });
    }
    show(0);
    var timer = null;
    function restart() {
      if (reduceMotion) return;
      window.clearInterval(timer);
      timer = window.setInterval(function () { show(index + 1); }, 4200);
    }
    ticks.forEach(function (tick, i) {
      tick.style.cursor = 'pointer';
      tick.addEventListener('click', function () { show(i); restart(); });
    });
    restart();

    /* The real thing. Returns false if the markup it needs is not on the page,
       so the caller can fall back rather than leave the stage frozen. */
    function journey() {
      var gsap = window.gsap;
      if (!visuals.length) return false;

      // The rail, pulse and scan line were refs in the component and carry no
      // data attribute; their arbitrary shadow utilities are unique enough.
      var pulse = $('[class*="0_0_24px_8px"]');
      var scan = $('[class*="0_0_18px_5px"]');
      if (!pulse || !scan) return false;
      var rail = pulse.previousElementSibling;
      var route = pulse.parentNode && pulse.parentNode.parentNode;
      if (!rail || !route) return false;

      var draws = $$('[data-process-draw]');
      var spinners = $$('[data-process-spinner]');
      var flows = $$('[data-process-flow]');
      var jerseyCore = $$('[data-process-jersey]');
      var jerseyPaths = $$('[data-jersey-path]');
      var measures = $$('[data-measure-line]');
      var orbits = $$('[data-kit-orbit]');

      // Hands the phases to GSAP: the stylesheet's own crossfade would fight
      // it for opacity, and its `transform: none !important` cancelled the
      // rise outright.
      document.body.classList.add('js-process-gsap');

      function dash(el) {
        if (!el || typeof el.getTotalLength !== 'function') return 0;
        try { return el.getTotalLength(); } catch (err) { return 0; }
      }

      gsap.set(steps, { autoAlpha: 0, yPercent: 24, rotateX: 14 });
      gsap.set(ticks, { autoAlpha: 0.3, scale: 0.86 });
      if (ticks[0]) gsap.set(ticks[0], { autoAlpha: 1, scale: 1 });
      gsap.set(stars, { autoAlpha: 0, scale: 0.72, rotate: -14, transformOrigin: '50% 50%' });
      if (stars[0]) gsap.set(stars[0], { autoAlpha: 0.85, scale: 1, rotate: 0 });
      gsap.set(visuals, { autoAlpha: 0, scale: 0.88, transformOrigin: '50% 50%' });
      gsap.set(visuals[0], { autoAlpha: 0.95, scale: 1 });
      gsap.set(jerseyCore, { autoAlpha: 1, scale: 1, transformOrigin: '50% 50%' });
      gsap.set(spinners, { transformOrigin: '50% 50%' });
      gsap.set(flows, { strokeDashoffset: 70 });
      draws.forEach(function (line) {
        var len = dash(line);
        if (len) gsap.set(line, { strokeDasharray: len, strokeDashoffset: len });
      });
      gsap.set(measures, { scaleX: 0, transformOrigin: '50% 50%' });
      jerseyPaths.forEach(function (path) {
        var len = dash(path);
        if (len) gsap.set(path, { strokeDasharray: len, strokeDashoffset: len });
      });

      // Never-ending loop, as the client asked for. `repeatRefresh` is
      // deliberately absent -- it re-records every start value each pass and
      // left phase 01 hidden, cycling only 02 to 04. `onRepeat` restores the
      // base state explicitly instead.
      function resetSteps() {
        gsap.set(steps, { autoAlpha: 0, yPercent: 24, rotateX: 14 });
        gsap.set(ticks, { autoAlpha: 0.3, scale: 0.86, color: '' });
        if (ticks[0]) gsap.set(ticks[0], { autoAlpha: 1, scale: 1 });
        gsap.set(stars, { autoAlpha: 0, scale: 0.72, rotate: -14 });
        if (stars[0]) gsap.set(stars[0], { autoAlpha: 0.85, scale: 1, rotate: 0 });
        gsap.set(visuals, { autoAlpha: 0, scale: 0.88 });
        gsap.set(visuals[0], { autoAlpha: 0.95, scale: 1 });
        // The rail, pulse, scan and orbits were left out of this reset, so at
        // the repeat they sat at their end values and the next pass tweened
        // them *backwards* from full to 25% — which is what made the tick look
        // like it had skipped a phase, and the whole loop stutter.
        gsap.set(rail, { scaleX: 0 });
        gsap.set(pulse, { x: 0 });
        gsap.set(scan, { yPercent: 0 });
        if (orbits.length) gsap.set(orbits, { rotate: 0 });
        if (measures.length) gsap.set(measures, { scaleX: 1 });
      }

      var timeline = gsap.timeline({
        defaults: { ease: 'power3.inOut' },
        paused: true,
        repeat: -1,
        // No gap at the loop point. The old 0.45s delay froze the finished
        // frame before the hard reset, which read as a hitch.
        repeatDelay: 0,
        onRepeat: resetSteps
      });

      var stepSpacing = 1.5;
      var sequenceDuration = steps.length * stepSpacing;
      var sceneIn = { autoAlpha: 0.95, scale: 1, duration: 0.24, ease: 'power3.out', overwrite: 'auto' };
      var sceneOut = { autoAlpha: 0, scale: 0.9, duration: 0.12, ease: 'power2.out', overwrite: 'auto' };

      if (jerseyPaths.length) timeline.to(jerseyPaths, { strokeDashoffset: 0, duration: 0.72, stagger: 0.045, ease: 'power2.inOut' }, 0);
      var firstDraws = visuals[0].querySelectorAll('[data-process-draw]');
      var firstFlows = visuals[0].querySelectorAll('[data-process-flow]');
      if (firstDraws.length) timeline.to(firstDraws, { strokeDashoffset: 0, duration: 0.62, stagger: 0.035, ease: 'power2.inOut' }, 0);
      if (firstFlows.length) timeline.to(firstFlows, { strokeDashoffset: -35, duration: 0.72, ease: 'none' }, 0);
      if (measures.length) timeline.to(measures, { scaleX: 1, duration: 0.6, stagger: 0.08, ease: 'power3.out' }, 0.1);
      if (orbits[0]) timeline.to(orbits[0], { rotate: 420, duration: sequenceDuration, ease: 'none' }, 0);
      if (orbits[1]) timeline.to(orbits[1], { rotate: -310, duration: sequenceDuration, ease: 'none' }, 0);
      timeline.to(scan, { yPercent: 720, duration: sequenceDuration, ease: 'none' }, 0);

      /* At 0, not 0.17. Mid-cycle a phase enters while the previous one is
         still fading, but phase 04's exit belongs to the end of the previous
         pass, so holding 01 back left a blank frame at the loop point. */
      timeline.fromTo(steps[0], { yPercent: 24, rotateX: 14 },
        { autoAlpha: 1, yPercent: 0, rotateX: 0, duration: 0.52, ease: 'power3.out' }, 0);

      steps.forEach(function (_, index) {
        var at = index * stepSpacing;
        var visual = visuals[index];
        var visualDraws = visual ? visual.querySelectorAll('[data-process-draw]') : null;
        var visualSpinners = visual ? visual.querySelectorAll('[data-process-spinner]') : null;
        var visualFlows = visual ? visual.querySelectorAll('[data-process-flow]') : null;
        var sceneBoundary = Math.max(0, at - 0.01);

        var offVisuals = visuals.filter(function (_, i) { return i !== index; });
        var offStars = stars.filter(function (_, i) { return i !== index; });
        if (offVisuals.length) timeline.set(offVisuals, { autoAlpha: 0, scale: 0.9 }, sceneBoundary);
        if (offStars.length) timeline.set(offStars, { autoAlpha: 0, scale: 0.72 }, sceneBoundary);
        if (jerseyCore.length) timeline.set(jerseyCore, { autoAlpha: index === 0 ? 1 : 0, scale: index === 0 ? 1 : 0.92 }, sceneBoundary);

        timeline
          .to(rail, { scaleX: (index + 1) / steps.length, duration: 0.78 }, at)
          .to(pulse, { x: function () { return route.clientWidth * (index / (steps.length - 1)); }, duration: 0.58, ease: 'power2.inOut' }, at);
        if (ticks[index]) timeline.to(ticks[index], { autoAlpha: 1, scale: 1, color: '#fdbc29', duration: 0.28, ease: 'power2.out' }, at);
        if (stars[index]) timeline.to(stars[index], { autoAlpha: 0.9, scale: 1, rotate: 0, duration: 0.48, ease: 'power3.out' }, at + 0.06);
        if ($('[data-kit-core]')) {
          timeline.fromTo('[data-kit-core]',
            { filter: 'drop-shadow(0 0 0px rgba(253,188,40,0))' },
            { filter: 'drop-shadow(0 0 18px rgba(253,188,40,.42))', duration: 0.22, yoyo: true, repeat: 1, ease: 'power2.out' }, at + 0.16);
        }

        if (visual) {
          timeline.to(visual, sceneIn, at + 0.04);
          if (visualDraws && visualDraws.length) timeline.to(visualDraws, { strokeDashoffset: 0, duration: 0.58, stagger: 0.025, ease: 'power2.inOut' }, at + 0.05);
          if (visualSpinners && visualSpinners.length) timeline.to(visualSpinners, { rotate: index % 2 ? -150 : 150, duration: 0.78, ease: 'power2.inOut' }, at + 0.05);
          if (visualFlows && visualFlows.length) timeline.to(visualFlows, { strokeDashoffset: -50, duration: 0.78, ease: 'none' }, at + 0.05);
        }

        if (index > 0) {
          timeline.to(steps[index - 1], { autoAlpha: 0, yPercent: -22, rotateX: -16, duration: 0.3, ease: 'power2.in' }, at);
          if (ticks[index - 1]) timeline.to(ticks[index - 1], { autoAlpha: 0.38, scale: 0.9, duration: 0.3 }, at);
          if (stars[index - 1]) timeline.to(stars[index - 1], { autoAlpha: 0, scale: 1.22, rotate: 16, duration: 0.32, ease: 'power2.in' }, at);
          timeline.fromTo(steps[index], { yPercent: 24, rotateX: 14 }, { autoAlpha: 1, yPercent: 0, rotateX: 0, duration: 0.52, ease: 'power3.out' }, at + 0.17);
          if (visuals[index - 1]) timeline.to(visuals[index - 1], sceneOut, at - 0.04);
        }
      });
      /* The loop point is a phase handoff like any other: the last phase
         exits with the same motion the mid-cycle transitions use, the first
         phase enters on top of it, and the rail, pulse, scan and orbits wind
         home underneath. onRepeat's reset then lands on an identical frame,
         so the repeat is invisible. */
      var tail = steps.length * stepSpacing;
      var last = steps.length - 1;
      timeline.to(steps[last], { autoAlpha: 0, yPercent: -22, rotateX: -16, duration: 0.3, ease: 'power2.in' }, tail);
      if (ticks[last]) timeline.to(ticks[last], { autoAlpha: 0.3, scale: 0.86, color: '', duration: 0.3 }, tail);
      if (stars[last]) timeline.to(stars[last], { autoAlpha: 0, scale: 1.22, rotate: 16, duration: 0.32, ease: 'power2.in' }, tail);
      if (visuals[last]) timeline.to(visuals[last], sceneOut, tail - 0.04);
      timeline.to(rail, { scaleX: 0, duration: 0.3, ease: 'power2.inOut' }, tail);
      timeline.to(pulse, { x: 0, duration: 0.3, ease: 'power2.inOut' }, tail);
      timeline.to(scan, { yPercent: 0, duration: 0.3, ease: 'power2.inOut' }, tail);
      if (orbits[0]) timeline.to(orbits[0], { rotate: 360, duration: 0.3, ease: 'power2.inOut' }, tail);
      if (orbits[1]) timeline.to(orbits[1], { rotate: -360, duration: 0.3, ease: 'power2.inOut' }, tail);
      /* Phase 01 is NOT re-entered here. It used to be, and then the timeline
         repeated and played it again from zero, so 01 sat on screen for 2.4s
         against 1.45s for every other phase -- the freeze the client saw. The
         repeat is the handoff now. */

      // Time-based, not scrubbed: it runs while the stage is on screen and
      // idles when it is not, so it never pins the page.
      var stage = steps[0].closest('section') || steps[0].parentNode;
      function play() { if (!timeline.isActive()) timeline.play(); }
      function idle() { timeline.pause(); }

      /* Visibility comes from an IntersectionObserver rather than a
         ScrollTrigger. The deck below pins for 1911px, and ScrollTrigger
         measures this section with that pin reverted — it placed the start
         2606px above where the section actually sits, so the timeline never
         played. An observer reports what is on screen and cannot drift. */
      var onScreen = false;
      if (window.IntersectionObserver) {
        new window.IntersectionObserver(function (entries) {
          onScreen = entries[0].isIntersecting;
          if (onScreen && !document.hidden) play(); else idle();
        }, { rootMargin: '-12% 0px -12% 0px' }).observe(stage);
      } else {
        onScreen = true;
        play();
      }
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) idle();
        else if (onScreen) play();
      });

      // Already in view on load -- start straight away.
      var bounds = stage.getBoundingClientRect();
      if (bounds.top < window.innerHeight * 0.88 && bounds.bottom > 0) play();

      return true;
    }
  })();

  /* ── Collection deck ────────────────────────────────────────────────────
     Ten cards stacked on top of one another; the inactive ones sit off to
     the right, rotated and clipped, exactly as the build arranges them. The
     numbered list on the left jumps between them. */
  (function deckBoot() {
    /* The home page injects its lower half after first paint, and the
       collection deck lives in that half — so this module used to run, find no
       cards and return for good. The section then had no behaviour at all,
       which is why it sat dead on a phone and the page simply scrolled past
       it. Wait for the cards, then set up exactly once. */
    if (document.querySelector('[data-collection-card]')) return collectionDeck();
    var tries = 0;
    var timer = window.setInterval(function () {
      if (document.querySelector('[data-collection-card]')) {
        window.clearInterval(timer);
        collectionDeck();
      } else if (++tries > 100) {          // ~20s, then give up quietly
        window.clearInterval(timer);
      }
    }, 200);
  })();

  function collectionDeck() {
    var cards = $$('[data-collection-card]');
    if (cards.length < 2) return;
    var jumps = $$('[data-collection-jump]');
    var counter = $('[data-collection-counter]');
    var deck = cards[0].parentNode;
    if (deck) deck.classList.add('deck-stage');

    // Under 768px the build renders a different tree: no stack, no pin, just a
    // horizontal snap carousel of 82vw cards. The deck's inline transform,
    // clip-path and pointer-events belong to the desktop stack only — left on,
    // they clipped 35% off every card and made all but the first untappable.
    var phone = window.matchMedia('(max-width: 767px)');
    var isPhone = function () { return phone.matches; };

    function clearDeckStyles() {
      cards.forEach(function (card) {
        card.classList.add('deck-card');
        card.style.transform = '';
        card.style.clipPath = '';
        card.style.pointerEvents = '';
        card.style.zIndex = '';
      });
    }

    var index = 0;
    function show(next) {
      index = Math.max(0, Math.min(cards.length - 1, next));
      if (isPhone()) { clearDeckStyles(); return; }
      cards.forEach(function (card, i) {
        card.classList.add('deck-card');
        card.style.zIndex = String(i + 1);
        var active = i === index;
        card.style.transform = active ? 'none' : 'translateX(112%) rotate(5deg)';
        card.style.clipPath = active ? 'inset(0 0 0 0)' : 'inset(0 0 0 35%)';
        card.style.pointerEvents = active ? 'auto' : 'none';
      });
      jumps.forEach(function (jump, i) {
        jump.style.opacity = i === index ? '1' : '0.28';
        jump.style.transform = i === index ? 'translateX(12px)' : 'translateX(0)';
      });
      if (counter) counter.textContent = String(index + 1).padStart(2, '0');
    }

    var deckST = null;
    jumps.forEach(function (jump, i) {
      jump.addEventListener('click', function (e) {
        e.preventDefault();
        show(i);
        // The section is pinned to scroll progress. Only painting the card
        // left the progress where it was, so the next scroll snapped straight
        // back — the jump has to move the scroll position with it.
        if (deckST) {
          var prog = (i + 0.5) / cards.length;
          document.scrollingElement.scrollTop =
            deckST.start + prog * (deckST.end - deckST.start);
        }
      });
    });
    show(0);
    // Crossing the breakpoint has to repaint the cards, or a phone rotated to
    // landscape keeps the flat carousel and a desktop keeps the cleared stack.
    var onBreakpoint = function () { show(isPhone() ? 0 : index); };
    if (phone.addEventListener) phone.addEventListener('change', onBreakpoint);
    else if (phone.addListener) phone.addListener(onBreakpoint);

    /* On a phone the deck becomes a plain snap carousel, which sat completely
       still — nothing moved, so the section read as broken and the page just
       scrolled past it. It now drifts like the product rails: slow, and it
       hands control straight back the moment a finger touches it. */
    (function deckDrift() {
      if (reduceMotion || !deck) return;

      /* Deliberately free of pointer state. An earlier version tracked
         `pointerdown`/`pointerup`, and a press whose release the element never
         saw — a drag that ends outside it, a cancelled touch — latched it into
         "interacting" forever and the drift never ran again. Instead it
         remembers the position it last wrote: if what is there now differs,
         a person moved it, so it yields and resumes from wherever they left
         off. Nothing can get stuck. */
      var written = null;
      var pos = 0;
      var yieldUntil = 0;
      var last = 0;
      var lastTick = 0;

      var advance = function (delta) {
        lastTick = Date.now();
        if (!isPhone()) { written = null; return; }
        var span = deck.scrollWidth - deck.clientWidth;
        if (span < 8) { written = null; return; }

        var box = deck.getBoundingClientRect();
        if (box.bottom < 0 || box.top > window.innerHeight) { written = null; return; }

        var current = deck.scrollLeft;
        if (written !== null && Math.abs(current - written) > 1.5) {
          // Someone swiped. Stand off, then carry on from where they stopped.
          yieldUntil = Date.now() + 1500;
          written = null;
        }
        if (Date.now() < yieldUntil) return;

        if (written === null) pos = current;
        pos += (span / 55) * (delta / 1000);      // one full pass in ~26s
        if (pos >= span - 1) pos = 0;
        deck.scrollLeft = pos;
        written = deck.scrollLeft;
      };

      window.requestAnimationFrame(function step(now) {
        var delta = last ? Math.min(now - last, 120) : 16;
        last = now;
        window.requestAnimationFrame(step);
        advance(delta);
      });

      /* Same watchdog the product rails carry. requestAnimationFrame is the
         right driver while the page is being painted, but it does not fire at
         all when the tab is throttled or backgrounded, and it stalls behind a
         long scroll on some mobile browsers. Without this the carousel simply
         stops and the section looks broken again. */
      window.setInterval(function () {
        var since = Date.now() - lastTick;
        if (since < 260) return;                 // frames are landing; leave it alone
        // Catch up in small steps rather than one large one. Applying the whole
        // stalled duration at once is what made the carousel crawl and then
        // visibly jump.
        var budget = Math.min(since, 600);
        while (budget > 0) {
          advance(Math.min(budget, 32));
          budget -= 32;
        }
      }, 120);
    })();

    /* Scroll lock. The built site pinned this section with ScrollTrigger and
       advanced the deck from scroll progress over 2.75 viewport heights — the
       capture kept GSAP's `.pin-spacer` wrapper but nothing pinned, so the
       reserved scroll distance showed up as a band of blank page instead.
       Pinning for real reinstates the behaviour and the spacer earns its
       height back. */
    /* The built site shows a short line under the heading on phones, where the
     numbered collection index is hidden. The capture only ever recorded the
     desktop tree, so it has to be put back. */
  (function deckIntro() {
    if (!window.matchMedia('(max-width: 767px)').matches) return;
    var grid = document.querySelector('[data-deck-grid]');
    if (!grid || grid.querySelector('.deck-intro')) return;
    var h2 = grid.querySelector('h2');
    if (!h2) return;
    var p = document.createElement('p');
    p.className = 'deck-intro text-slate-ink';
    p.textContent = 'Swipe through the drops and tap any collection to shop it.';
    h2.parentNode.appendChild(p);
  })();

  (function scrollLock() {
      if (reduceMotion) return;
      if (!window.gsap || !window.ScrollTrigger) return;
      // Not on a phone. ScrollTrigger's pin wraps the section in a `.pin-spacer`
      // and writes an explicit width onto it — measured at desktop, it came out
      // 1521px inside a 419px viewport, which pushed the artwork column clean
      // off-screen and made the section look like it had no pictures at all.
      // Scroll-jacking a phone is the wrong interaction anyway; the numbered
      // list still switches cards.
      var section = cards[0].closest('section');
      if (!section) return;

      // The captured spacer is inert markup; ScrollTrigger makes its own.
      //
      // This has to happen BEFORE the phone check, not after it. The capture
      // shipped a `.pin-spacer` around this section with a desktop pin's height
      // baked into it -- close to three viewports of nothing. On desktop
      // ScrollTrigger replaced it, so it was never seen. On a phone the code
      // returned before reaching this line and the dead spacer stayed in the
      // document, which is the stretch of empty screen you scroll through three
      // to five times before the section moves.
      var stale = section.parentNode;
      if (stale && stale.classList && stale.classList.contains('pin-spacer')) {
        stale.parentNode.insertBefore(section, stale);
        stale.parentNode.removeChild(stale);
      }

      // Scroll-jacking a phone is the wrong interaction, and the pin's spacer
      // is measured at desktop width, so it is skipped there.
      if (window.matchMedia('(max-width: 767px)').matches) return;

      gsap.registerPlugin(ScrollTrigger);
      deckST = ScrollTrigger.create({
        trigger: section,
        start: 'top top',
        end: function () { return '+=' + Math.round(window.innerHeight * 2.75); },
        pin: true,
        pinSpacing: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: function (self) {
          // Progress maps across the cards, with the last one holding to the end.
          var i = Math.min(cards.length - 1, Math.floor(self.progress * cards.length));
          if (i !== index) show(i);
        }
      });
    })();
  }

  /* ── Recent projects carousel ───────────────────────────────────────────
     All six photographs are already stacked in the markup; only the active one
     is opaque. The words belong to whichever project React had on the stage
     when the page was captured, so they come from projects-data instead. */
  (function projects() {
    var stage = $('[data-project-stage]');
    var copyEl = $('[data-projects-copy]');
    if (!stage || !copyEl) return;

    var data;
    try { data = JSON.parse(copyEl.textContent); } catch (err) { return; }

    // Matched by shape, not by an escaped Tailwind class name: the six stacked
    // photographs are the absolutely positioned images inside the stage.
    var shots = $$('img', stage).filter(function (img) {
      return /absolute/.test(img.className) && /inset-0/.test(img.className);
    });
    if (shots.length < 2) return;

    var title = stage.querySelector('h3');
    if (!title) return;
    var panel = title.parentNode;
    var type = title.nextElementSibling;
    var bodyWrap = type && type.nextElementSibling;
    var link = panel.querySelector('a[href]');

    // "01 / 06" — found by its text, since several spans share its classes.
    var counter = null;
    $$('span', stage).some(function (el) {
      if (!el.children.length && /^\s*\d+\s*\/\s*\d+\s*$/.test(el.textContent || '')) {
        counter = el; return true;
      }
      return false;
    });

    var index = 0;
    function pad(n) { return (n < 10 ? '0' : '') + n; }

    function show(next) {
      index = (next + shots.length) % shots.length;
      shots.forEach(function (img, i) {
        img.classList.toggle('opacity-100', i === index);
        img.classList.toggle('opacity-0', i !== index);
      });
      var item = data[index];
      if (!item) return;
      title.textContent = item.title;
      if (type) type.textContent = item.type;
      if (bodyWrap) {
        bodyWrap.innerHTML = '';
        item.body.forEach(function (text) {
          var p = document.createElement('p');
          p.className = 'text-sm leading-relaxed text-white/72 md:text-[0.95rem]';
          p.textContent = text;
          bodyWrap.appendChild(p);
        });
      }
      if (link) {
        if (item.href) { link.href = item.href; link.style.display = ''; }
        else { link.style.display = 'none'; }
      }
      if (counter) counter.textContent = pad(index + 1) + ' / ' + pad(shots.length);
    }

    // Swipe the stage itself — on a phone there is no cursor to reach the
    // arrows with mid-scroll, and the build's carousel was throwable anyway.
    stage.style.touchAction = 'pan-y';
    var swipeX = null, swiped = false;
    stage.addEventListener('pointerdown', function (e) { swipeX = e.clientX; swiped = false; });
    stage.addEventListener('pointermove', function (e) {
      if (swipeX === null) return;
      if (Math.abs(e.clientX - swipeX) > 6) e.preventDefault();
    });
    ['pointerup', 'pointercancel'].forEach(function (type) {
      stage.addEventListener(type, function (e) {
        if (swipeX === null) return;
        var dx = (e.clientX || 0) - swipeX;
        swipeX = null;
        if (Math.abs(dx) >= 45) { swiped = true; show(index + (dx < 0 ? 1 : -1)); }
      });
    });
    stage.addEventListener('click', function (e) {
      if (swiped) { e.preventDefault(); e.stopPropagation(); swiped = false; }
    }, true);

    var scope = stage.closest('section') || document;
    $$('[aria-label="Previous project"]', scope).forEach(function (b) {
      b.addEventListener('click', function (e) { e.preventDefault(); show(index - 1); });
    });
    $$('[aria-label="Next project"]', scope).forEach(function (b) {
      b.addEventListener('click', function (e) { e.preventDefault(); show(index + 1); });
    });

    // The thumbnail strip underneath jumps straight to a project.
    $$('[data-project-stage]', scope).forEach(function (row) {
      if (row === stage) return;
      $$('button, a', row).forEach(function (thumb, i) {
        thumb.addEventListener('click', function (e) { e.preventDefault(); show(i); });
      });
    });

    show(0);
  })();

  /* ── Ambition banner aperture ───────────────────────────────────────────
     Six navy blades sit over the video as a closed iris and swing open to
     reveal it — this is the section's entrance on the built site. GSAP set
     their rotation at runtime, and the converter strips transforms, so all six
     ended up stacked dead-centre painting a navy block across the video. */
  (function aperture() {
    var blades = $$('[data-aperture-blade]');
    if (!blades.length) return;
    var stage = blades[0].parentNode;

    // Closed: each blade is a triangle rotated 60 degrees from the last.
    blades.forEach(function (blade, i) {
      blade.style.clipPath = 'polygon(50% 100%, 0 0, 100% 0)';
      blade.style.transform = 'translate(-50%, -100%) rotate(' + (i * 60) + 'deg)';
      blade.style.transition = 'transform 720ms cubic-bezier(.65,0,.35,1), opacity 720ms cubic-bezier(.65,0,.35,1)';
      blade.style.transitionDelay = (i * 25) + 'ms';
    });

    function open() {
      blades.forEach(function (blade, i) {
        var out = i % 2 ? 42 : -42;
        blade.style.transform = 'translate(-50%, -100%) rotate(' + out + 'deg) scale(1.45)';
        blade.style.opacity = '0';
      });
      // Once open they must not intercept anything, and a blade left at
      // opacity 0 over the video still costs a compositing layer.
      window.setTimeout(function () {
        blades.forEach(function (blade) { blade.style.display = 'none'; });
      }, 1200);
    }

    if (reduceMotion || !('IntersectionObserver' in window)) { open(); return; }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.disconnect();
        open();
      });
    }, { threshold: 0.25 });
    io.observe(stage);

    // Backstop, but only for a stage that is actually on screen. A blind timer
    // opened the iris long before the section was reached, so the reveal was
    // always over by the time you scrolled to it — which reads as "no
    // animation". Below the fold it stays shut and waits for the observer.
    window.setInterval(function () {
      if (!blades.length || blades[0].style.display === 'none') return;
      var r = stage.getBoundingClientRect();
      // A quarter of the section on screen, matching the observer — 1px of
      // visibility opened the iris early, so the reveal was over before the
      // section was actually reached (worst on a phone, where sections peek).
      if (r.top < window.innerHeight * 0.75 && r.bottom > window.innerHeight * 0.25) open();
    }, 2000);
  })();

  /* ── Video ──────────────────────────────────────────────────────────────
     Autoplay only works muted, and React set `muted` as a property so the
     attribute never made it into the markup. Set it on the element and ask to
     play once the video is on screen. */
  (function videos() {
    var vids = $$('video');
    if (!vids.length) return;

    vids.forEach(function (v) {
      v.muted = true;
      v.defaultMuted = true;
      v.playsInline = true;
      v.setAttribute('muted', '');
      v.setAttribute('playsinline', '');
      // Shopify serves theme .mp4 assets as `application/mp4`. Chrome will not
      // pick a decoder from that on the element's own `src`, so the video sat
      // at readyState 0 and painted as a flat coloured block over the section.
      // Moving the URL onto a <source> with an explicit type tells it what to
      // use, which is what the built site does.
      var url = v.getAttribute('src');
      if (url && !v.querySelector('source')) {
        v.removeAttribute('src');
        var source = document.createElement('source');
        source.setAttribute('src', url);
        source.setAttribute('type', 'video/mp4');
        v.insertBefore(source, v.firstChild);
      }
      v.preload = 'auto';
      try { v.load(); } catch (err) {}

      // Shopify serves theme .mp4 assets as `application/mp4` with
      // `x-content-type-options: nosniff`, so the browser will not pick a
      // decoder and the element paints as a flat coloured block over the
      // section. If nothing has decoded shortly after load, fall back to the
      // poster still the markup already carries — a correct frame beats a hole.
      window.setTimeout(function () {
        if (v.readyState > 0 || v.videoWidth > 0) return;
        // Still downloading is not a failure: an 8MB banner needs longer
        // than the old three-second cut-off, which swapped the poster in
        // before the video ever had a chance to play.
        if (v.networkState === 2 && !v.error) return;
        var poster = v.getAttribute('poster');
        if (!poster) return;
        var img = document.createElement('img');
        img.src = poster;
        img.alt = '';
        img.className = v.className;
        img.setAttribute('loading', 'lazy');
        img.style.objectFit = window.getComputedStyle(v).objectFit || 'cover';
        img.style.width = '100%';
        img.style.height = '100%';
        // Starts visible. A zero-opacity start relies on a follow-up frame that
        // a throttled tab may never run, and the section would stay empty —
        // exactly the hole this fallback exists to remove. The scale settles in
        // on its own and needs no second frame to become visible.
        // No entrance animation here. `both` fill holds the from-state until the
        // animation starts, and an element inserted off-screen does not start
        // until it is rendered — which left the banner sitting at 55% opacity.
        // A visible banner matters more than a flourish.
        img.style.opacity = '1';
        if (v.parentNode) {
          v.parentNode.insertBefore(img, v);
          v.remove();
        }
      }, 12000);
    });

    function play(v) {
      var attempt = v.play();
      // A rejected play() is normal before any user gesture; retry on the
      // first interaction rather than leaving a dead poster frame.
      if (attempt && attempt.catch) {
        attempt.catch(function () {
          var once = function () {
            v.play().catch(function () {});
            document.removeEventListener('pointerdown', once);
            document.removeEventListener('scroll', once);
          };
          document.addEventListener('pointerdown', once, { once: true });
          document.addEventListener('scroll', once, { once: true });
        });
      }
    }

    if (!('IntersectionObserver' in window)) { vids.forEach(play); return; }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) play(entry.target);
        else entry.target.pause();
      });
    }, { rootMargin: '200px' });
    vids.forEach(function (v) { io.observe(v); });
  })();

  /* -- Closing CTA banner -------------------------------------------------
     The built site opens this one with an iris: the panel is clipped to a
     small circle, the photograph sits pushed in and over-scaled, and the copy
     is blurred out. As the section comes up the circle expands to full bleed,
     the photograph settles back, the orbit ring turns and the copy sharpens.
     Ported from the CTA block of the source; the generic rise was standing in
     for it, which is why the banner simply appeared. */
  (function ctaBanner() {
    var content = $('[data-cta-content]');
    if (!content) return;
    var iris = content.parentElement;
    if (!iris) return;
    var photo = $('img', iris);
    var orbit = $$('[data-cta-orbit]', iris);
    var actions = $$('[data-cta-action]', content);
    var section = iris.closest('section') || iris;

    // The generic reveal must not own these as well.
    [content].concat(actions, orbit).forEach(function (el) {
      el.classList.remove('will-rise', 'has-risen');
    });

    var gsap = window.gsap;
    if (!gsap) return;

    function setDone() {
      gsap.set(iris, { clipPath: 'circle(84% at 50% 50%)' });
      if (photo) gsap.set(photo, { scale: 1.02, xPercent: -2 });
      gsap.set(content, { autoAlpha: 1, scale: 1, y: 0, filter: 'blur(0px)' });
      if (actions.length) gsap.set(actions, { autoAlpha: 1, y: 0, filter: 'blur(0px)' });
      if (orbit.length) gsap.set(orbit, { autoAlpha: 1, rotate: 150, scale: 1.35 });
      section.setAttribute('data-revealed', 'true');
    }

    if (reduceMotion) { setDone(); return; }

    gsap.set(iris, { clipPath: 'circle(8% at 50% 50%)' });
    if (photo) gsap.set(photo, { scale: 1.12, xPercent: 2.5, transformOrigin: '58% 50%' });
    gsap.set(content, { autoAlpha: 0, scale: 0.94, y: 18, filter: 'blur(10px)' });
    if (actions.length) gsap.set(actions, { autoAlpha: 0, y: 18, filter: 'blur(8px)' });
    if (orbit.length) gsap.set(orbit, { autoAlpha: 0.34, rotate: -18, scale: 0.82 });

    var started = false;
    var completed = false;
    function playReveal() {
      if (completed) { setDone(); return; }
      if (started) return;
      started = true;
      var tl = gsap.timeline({
        defaults: { ease: 'power3.out' },
        onComplete: function () { completed = true; setDone(); }
      });
      tl.to(iris, { clipPath: 'circle(84% at 50% 50%)', duration: 0.58, ease: 'power3.inOut' }, 0);
      if (photo) tl.to(photo, { scale: 1.02, xPercent: -2, duration: 0.68, ease: 'power2.out' }, 0);
      if (orbit.length) tl.to(orbit, { autoAlpha: 1, rotate: 150, scale: 1.35, duration: 0.68, ease: 'power2.out' }, 0.02);
      tl.to(content, { autoAlpha: 1, scale: 1, y: 0, filter: 'blur(0px)', duration: 0.36 }, 0.1);
      if (actions.length) tl.to(actions, { autoAlpha: 1, y: 0, filter: 'blur(0px)', stagger: 0.04, duration: 0.28 }, 0.24);

      // The start state hides the copy outright, so a timeline that never
      // ticks -- a starved rAF, a backgrounded tab restored mid-reveal --
      // would leave the banner blank. Force the finished state either way.
      window.setTimeout(function () {
        if (completed) return;
        tl.kill();
        completed = true;
        setDone();
      }, 2200);
    }

    // A bounds check on scroll, exactly as the source does it. No
    // ScrollTrigger: the deck below pins for 1911px and triggers measured
    // around it land in the wrong place.
    function whenApproaching() {
      if (completed) return;
      var b = section.getBoundingClientRect();
      if (b.top < window.innerHeight * 1.06 && b.bottom > window.innerHeight * -0.05) playReveal();
    }
    window.addEventListener('scroll', whenApproaching, { passive: true });
    window.addEventListener('resize', whenApproaching);
    // The panel must never be left clipped shut if the scroll handler is
    // starved -- a banner stuck at 8% would hide the copy outright.
    window.setInterval(whenApproaching, 900);
    whenApproaching();
  })();

  /* ── Scroll reveals ─────────────────────────────────────────────────────
     The build's entrance animations were GSAP timelines that are not here any
     more. This restores the same idea — content rises and fades as its section
     comes up — on the inner elements rather than whole sections, so a missed
     observer can never blank out a screenful. A timer sweeps up anything still
     hidden as a backstop. */
  (function reveals() {
    if (reduceMotion || !('IntersectionObserver' in window)) return;

    var main = $('main');
    if (!main) return;

    var sections = $$('section', main);
    var groups = [];

    sections.forEach(function (section, sectionIndex) {
      // The hero is above the fold and drives its own animation.
      if (sectionIndex === 0 || section.querySelector('[data-hero-media]')) return;
      var picks = $$(
        ':scope > * > .eyebrow, :scope > * > h2, :scope > * > h3, :scope > * > p,' +
        ':scope article, :scope > * > a, :scope figure, :scope li',
        section
      ).filter(function (el) {
        // Anything another module owns must be left alone. The backstop below
        // force-shows every element it tracks, so sweeping up a hero slide or a
        // process phase un-hides all of them at once and they stack.
        // Product cards live in a rail that scrolls sideways, so "in view" is
        // decided by the rail's own scroll position, not the page's. Fading
        // them individually left whichever cards the drift had carried in
        // sitting at opacity 0 until an observer happened to catch up — a
        // screenful of blank white. The rail animates as a whole instead.
        if (el.closest('[data-hero-copy], [data-hero-media], [data-process-step], [data-collection-card], [data-product-slot], .deck-card, .preloader')) return false;
        return el.offsetHeight > 0 && el.offsetHeight < 1400;
      }).slice(0, 24);
      if (picks.length) groups.push(picks);
    });

    if (!groups.length) return;

    var all = [];
    groups.forEach(function (picks) {
      picks.forEach(function (el, i) {
        el.classList.add('will-rise');
        el.style.transitionDelay = Math.min(i, 6) * 70 + 'ms';
        all.push(el);
      });
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('has-risen');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.05 });

    all.forEach(function (el) { io.observe(el); });

    // Backstop, viewport-aware. The old version added `has-risen` to every
    // tracked element after four seconds, so everything below the fold was
    // already revealed before it was scrolled to and no section ever animated
    // in. This only rescues elements that are on screen and still hidden.
    window.setInterval(function () {
      all.forEach(function (el) {
        if (el.classList.contains('has-risen')) return;
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('has-risen');
      });
    }, 1200);
  })();

  /* ── Counters ───────────────────────────────────────────────────────────
     Stat figures count up the first time they are seen. */
  (function counters() {
    // The build marks its stat figures with `tabular-nums` (useCountUp) — there
    // is no data-count-to attribute in the ported markup, which is why every
    // number sat at its final value and never counted.
    var stats = $$('.tabular-nums').filter(function (el) {
      return !el.querySelector('*') && /^\d[\d\s,]*\D{0,3}$/.test((el.textContent || '').trim());
    });
    if (!stats.length || !('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        io.unobserve(el);

        var full = (el.textContent || '').trim();
        var match = full.match(/^(\d[\d,\s]*)(.*)$/);
        if (!match || reduceMotion) return;
        var target = Number(match[1].replace(/[,\s]/g, ''));
        var suffix = match[2];
        if (!target) return;

        // Fail-safe: if the frames stall the number must still read its real
        // value, never "0+".
        var settle = window.setTimeout(function () { el.textContent = full; }, 2200);
        var started = null;
        window.requestAnimationFrame(function tick(now) {
          if (started === null) started = now;
          var progress = Math.min((now - started) / 900, 1);
          var eased = 1 - Math.pow(1 - progress, 3);
          el.textContent = Math.round(target * eased).toLocaleString() + suffix;
          if (progress < 1) window.requestAnimationFrame(tick);
          else { window.clearTimeout(settle); el.textContent = full; }
        });
      });
    }, { threshold: 0.3, rootMargin: '0px 0px -10% 0px' });
    stats.forEach(function (el) { io.observe(el); });
  })();

  /* ── Mobile menu ────────────────────────────────────────────────────────
     One group open at a time, matching the site. */
  (function mobileMenu() {
    var toggle = $('[aria-controls="mobile-menu"]');
    var panel = document.getElementById('mobile-menu');
    if (toggle && panel) {
      toggle.addEventListener('click', function () {
        var open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!open));
        panel.classList.toggle('hidden', open);
      });
    }

    $$('[data-mobile-panel]').forEach(function (sub) {
      var button = sub.previousElementSibling;
      if (!button || button.tagName !== 'BUTTON') return;
      button.addEventListener('click', function () {
        var open = button.getAttribute('aria-expanded') === 'true';
        $$('[data-mobile-panel]').forEach(function (other) {
          other.classList.add('hidden');
          if (other.previousElementSibling) {
            other.previousElementSibling.setAttribute('aria-expanded', 'false');
          }
        });
        button.setAttribute('aria-expanded', String(!open));
        sub.classList.toggle('hidden', open);
      });
    });
  })();

  /* ── Search panel and cart drawer ───────────────────────────────────────
     Both are rendered after the footer, held off-screen by a transform. */
  (function overlays() {
    var search = $('[aria-label="Search products"]');
    var bag = $('[aria-label="Your bag"]');
    var scrim = search ? search.previousElementSibling : null;

    function open(el) {
      if (!el) return;
      el.classList.add('is-open');
      if (scrim) scrim.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      var field = el.querySelector('input');
      if (field) field.focus();
    }
    function closeAll() {
      [search, bag, scrim].forEach(function (el) { if (el) el.classList.remove('is-open'); });
      document.body.style.overflow = '';
    }

    var searchButton = $('button[aria-label="Search"]');
    var cartButton = $('button[aria-label="Cart"]');
    if (searchButton) searchButton.addEventListener('click', function () { open(search); });
    if (cartButton) cartButton.addEventListener('click', function () { open(bag); });

    $$('[aria-label="Close search"], [aria-label="Close bag"]').forEach(function (button) {
      button.addEventListener('click', closeAll);
    });
    if (scrim) scrim.addEventListener('click', closeAll);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeAll(); });

    // Search posts to Shopify's own results page.
    var form = search ? search.querySelector('form') : null;
    if (form) {
      form.setAttribute('action', '/search');
      form.setAttribute('method', 'get');
      var field = form.querySelector('input');
      if (field) field.setAttribute('name', 'q');
    }
  })();

  /* ── Product rails ──────────────────────────────────────────────────────
     ONE code path at every breakpoint, matching the build: the viewport is a
     REAL horizontal scroller, so a phone swipes it natively with its own
     momentum, a mouse drags it, and the automatic drift is JS writing
     `scrollLeft` that always yields to the user instead of fighting them.

     The port had done the opposite — `overflow-x: hidden` with the track moved
     by a transform — which took native touch scrolling away entirely. On a
     phone the cards could not be swiped at all, and on desktop the drift and
     the drag both wrote the same transform and fought each other. */
  (function rails() {
    var viewports = $$('.serotoninn-products__viewport');
    // Any stage whose markup predates the viewport element still gets whichever
    // child actually overflows.
    $$('[data-product-stage]').forEach(function (stage) {
      if (!stage.querySelector('.serotoninn-products__viewport')) {
        viewports.push(stage.querySelector('[class*="overflow"]') || stage);
      }
    });
    // The projects carousel and any hand-marked rail drag the same way.
    $$('[data-rail], .rail, .overflow-x-auto').forEach(function (el) { viewports.push(el); });

    viewports
      .filter(function (el, i, list) { return el && list.indexOf(el) === i; })
      .forEach(setupRail);

    function setupRail(rail) {
      var track = rail.querySelector('.serotoninn-products__track') || rail.firstElementChild;
      var stage = rail.closest('[data-product-stage]');
      var reverse = !!stage && stage.getAttribute('data-direction') === 'reverse';

      // Undo anything an earlier pass left inline, and make the scroller real.
      rail.style.overflowX = 'auto';
      rail.style.overflowY = 'hidden';
      rail.style.scrollbarWidth = 'none';
      rail.style.touchAction = 'pan-x pan-y';
      rail.style.cursor = 'grab';
      if (track) { track.style.transform = ''; track.style.willChange = 'auto'; }

      // The rail loops only when the track holds two identical copies of the
      // product set — then a shift of exactly one period lands on pixel
      // identical content and the drift is seamless in either direction.
      var slots = track ? $$('[data-product-slot]', track) : [];
      var looping = slots.length >= 4 && slots.length % 2 === 0;
      var duration = reverse ? 42 : 38;     // seconds to travel one period
      var period = 0;
      var pos = 0;
      var interacting = false;
      var lastUserSignal = 0;
      var running = false;

      function measure() {
        if (!track || !looping) { period = 0; return; }
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        period = (track.getBoundingClientRect().width + gap) / 2;
      }
      function clampScroll(v) {
        return Math.min(Math.max(v, 0), Math.max(rail.scrollWidth - rail.clientWidth, 0));
      }
      function wrap() {
        if (period <= 0) return;
        var buffer = period * 0.15;
        if (pos > rail.scrollWidth - rail.clientWidth - buffer) pos -= period;
        else if (pos < buffer) pos += period;
      }
      function markUser() { lastUserSignal = Date.now(); }

      // ── Drag to scroll. Mouse and pen only; touch keeps native scrolling,
      //    which is smoother and carries the platform's own momentum. ──
      var dragging = false, dragStartX = 0, dragStartScroll = 0, moved = 0;

      rail.addEventListener('dragstart', function (e) { e.preventDefault(); });
      rail.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch') { interacting = true; markUser(); return; }
        // Images and links are natively draggable, so a mouse press on a card
        // started an HTML5 drag and the browser stopped sending pointermove —
        // which is exactly why the rail could never be dragged.
        e.preventDefault();
        dragging = true;
        moved = 0;
        dragStartX = e.clientX;
        dragStartScroll = rail.scrollLeft;
        interacting = true;
        markUser();
        rail.style.cursor = 'grabbing';
        if (stage) stage.setAttribute('data-dragging', 'true');
      });
      rail.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        var dx = e.clientX - dragStartX;
        if (Math.abs(dx) > 3 && rail.hasPointerCapture && !rail.hasPointerCapture(e.pointerId)) {
          // Capture only once this is a genuine drag, so a plain click on a
          // product card still reaches the card.
          if (rail.setPointerCapture) rail.setPointerCapture(e.pointerId);
        }
        moved = Math.max(moved, Math.abs(dx));
        rail.scrollLeft = clampScroll(dragStartScroll - dx);
        markUser();
      });
      function endDrag(e) {
        if (!dragging) return;
        dragging = false;
        interacting = false;
        markUser();
        if (rail.hasPointerCapture && rail.hasPointerCapture(e.pointerId)) {
          rail.releasePointerCapture(e.pointerId);
        }
        rail.style.cursor = 'grab';
        if (stage) stage.removeAttribute('data-dragging');
        // Swallow the click that follows a real drag so it does not open a product.
        if (moved > 6) {
          var swallow = function (ev) { ev.preventDefault(); ev.stopPropagation(); };
          rail.addEventListener('click', swallow, true);
          window.setTimeout(function () {
            rail.removeEventListener('click', swallow, true);
          }, 60);
        }
      }
      rail.addEventListener('pointerup', endDrag);
      rail.addEventListener('pointercancel', endDrag);

      rail.addEventListener('touchstart', function () { interacting = true; markUser(); }, { passive: true });
      rail.addEventListener('touchend', function () { interacting = false; markUser(); }, { passive: true });
      rail.addEventListener('touchcancel', function () { interacting = false; markUser(); }, { passive: true });
      rail.addEventListener('wheel', markUser, { passive: true });
      rail.addEventListener('scroll', function () { if (!running) markUser(); }, { passive: true });
      window.addEventListener('resize', measure);

      // Both copies load eagerly so a wrap never lands on a lazy placeholder,
      // and nothing inside the rail may start a native drag of its own.
      if (track) {
        $$('img', track).forEach(function (img) { img.loading = 'eager'; img.draggable = false; });
        $$('a', track).forEach(function (a) { a.draggable = false; });
      }
      measure();
      if (period > 0) { pos = period; rail.scrollLeft = pos; }

      // ── Automatic drift. An enhancement only: with reduced motion the rail
      //    is still fully swipeable and draggable, just still. ──
      if (!reduceMotion && looping) {
        var lastTick = 0;
        var advance = function (delta) {
          if (period <= 0) { measure(); return; }
          // Any interaction, and the 900ms after it, hands control to the user.
          if (interacting || Date.now() - lastUserSignal < 900) { running = false; return; }
          if (!running) { running = true; pos = rail.scrollLeft; }
          pos += (period / duration) * (delta / 1000) * (reverse ? -1 : 1);
          wrap();
          rail.scrollLeft = pos;
        };
        var lastFrame = 0;
        window.requestAnimationFrame(function step(now) {
          var delta = lastFrame ? now - lastFrame : 16;
          lastFrame = now;
          lastTick = Date.now();
          window.requestAnimationFrame(step);
          advance(Math.min(delta, 120));
        });
        // requestAnimationFrame is the right driver while the page is being
        // painted, but it stalls behind a long scroll on some mobile browsers.
        // If no frame has landed for a while, keep the rail moving on a timer
        // so it never simply sits still.
        window.setInterval(function () {
          var since = Date.now() - lastTick;
          if (since > 400) { lastTick = Date.now(); advance(Math.min(since, 600)); }
        }, 200);
      }

      // The arrows beside the projects carousel move it a card at a time.
      var scope = rail.closest('section') || document;
      var stepBy = function (dir) {
        var card = rail.firstElementChild && rail.firstElementChild.firstElementChild;
        var by = card ? card.getBoundingClientRect().width + 24 : rail.clientWidth * 0.8;
        markUser();
        rail.scrollBy({ left: dir * by, behavior: reduceMotion ? 'auto' : 'smooth' });
      };
      $$('[aria-label="Previous project"], [data-rail-prev]', scope).forEach(function (b) {
        b.addEventListener('click', function (e) { e.preventDefault(); stepBy(-1); });
      });
      $$('[aria-label="Next project"], [data-rail-next]', scope).forEach(function (b) {
        b.addEventListener('click', function (e) { e.preventDefault(); stepBy(1); });
      });
    }
  })();

  /* ── Product page ───────────────────────────────────────────────────────
     Thumbnails swap the main image; the colour and size buttons pick a real
     variant, so the price, the hidden id the form posts and the photograph all
     follow the choice. */
  (function product() {
    var root = $('[data-product-root]');
    var data = $('[data-product-variants]');
    if (!root || !data) return;

    var variants;
    try { variants = JSON.parse(data.textContent); } catch (err) { return; }
    if (!variants.length) return;

    var main = $('[data-product-main]', root);
    var priceEl = $('[data-product-price]', root);
    var idInput = $('[data-variant-id]', root);
    var addBtn = $('[data-product-add]', root);
    var optionButtons = $$('[data-option-name]', root);

    // Which option slot each button drives, taken from the variant option
    // order rather than assumed — Colour is not always option 1.
    var names = [];
    optionButtons.forEach(function (b) {
      var n = b.getAttribute('data-option-name');
      if (names.indexOf(n) < 0) names.push(n);
    });

    var chosen = variants[0].options.slice();

    // Which slot of a variant's `options` array a value belongs to, derived
    // from the data rather than from the order the buttons happen to appear in.
    // Those two are not the same — Colour is option 2 on some products and
    // option 1 on others — and the mismatch meant a button was compared against
    // the wrong chosen value, so nothing ever highlighted.
    function slotFor(value) {
      for (var i = 0; i < variants.length; i++) {
        var idx = variants[i].options.indexOf(value);
        if (idx >= 0) return idx;
      }
      return -1;
    }

    function money(cents) {
      return 'R ' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    // The main image carries a `srcset`, and a browser always prefers that over
    // `src` — so picking a colour wrote a new `src` that the page then ignored
    // and the photograph never changed. Rewrite both, keeping the retina
    // candidates by re-pointing Shopify's own width parameter.
    function setMainImage(url) {
      if (!main || !url) return;
      if (/[?&]width=\d+/.test(url)) {
        main.srcset = [800, 1200, 1800, 2400].map(function (w) {
          return url.replace(/([?&]width=)\d+/, '$1' + w) + ' ' + w + 'w';
        }).join(', ');
      } else {
        main.removeAttribute('srcset');
      }
      main.src = url;
    }

    // A colour swap should light up that colour's thumbnail too, so the strip
    // does not keep pointing at whichever image was open before.
    function syncThumb(url) {
      if (!url) return;
      var base = url.split('?')[0];
      $$('[data-product-thumb]', root).forEach(function (t) {
        var hit = (t.getAttribute('data-src') || '').split('?')[0] === base;
        t.classList.toggle('border-gold', hit);
        t.classList.toggle('border-transparent', !hit);
      });
    }

    function match() {
      for (var i = 0; i < variants.length; i++) {
        var ok = true;
        for (var j = 0; j < chosen.length; j++) {
          if (chosen[j] != null && variants[i].options[j] !== chosen[j]) { ok = false; break; }
        }
        if (ok) return variants[i];
      }
      return null;
    }

    function sync() {
      var variant = match();
      optionButtons.forEach(function (b) {
        var slot = slotFor(b.getAttribute('data-option-value'));
        var on = slot >= 0 && chosen[slot] === b.getAttribute('data-option-value');
        if (on) b.setAttribute('data-selected', '1');
        else b.removeAttribute('data-selected');
      });
      if (!variant) return;
      if (idInput) idInput.value = variant.id;
      if (priceEl) priceEl.textContent = money(variant.price);
      setMainImage(variant.image);
      syncThumb(variant.image);
      /* The button is never disabled and never says "Sold out". The catalogue
         is made to order against a 10-piece MOQ — every variant is untracked
         and set to continue selling, so `available` can only ever be false
         through some accident of stock locations or market catalogues, and
         when it was, picking a size greyed the button out on every product in
         the store. There is no stock to run out of; leave the button live. */
    }

    optionButtons.forEach(function (b) {
      b.addEventListener('click', function () {
        var slot = slotFor(b.getAttribute('data-option-value'));
        if (slot < 0) return;
        chosen[slot] = b.getAttribute('data-option-value');
        sync();
      });
    });

    $$('[data-product-thumb]', root).forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        setMainImage(thumb.getAttribute('data-src'));
        $$('[data-product-thumb]', root).forEach(function (t) {
          t.classList.toggle('border-gold', t === thumb);
          t.classList.toggle('border-transparent', t !== thumb);
        });
      });
    });

    sync();
  })();

  /* ── Add to cart ────────────────────────────────────────────────────────
     Keeps the header count in step; falls back to a normal form post. */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form.matches('form[action*="/cart/add"]')) return;

    // "Buy now" posts `checkout`, which Shopify answers with a redirect to the
    // checkout. Intercepting that would swallow the redirect and look dead, so
    // it is left to submit normally.
    var by = event.submitter;
    if (by && by.name === 'checkout') return;

    event.preventDefault();

    var data = new FormData(form);
    if (by && by.name && by.name !== 'add') data.append(by.name, by.value || '');

    fetch('/cart/add.js', {
      method: 'POST',
      body: data,
      headers: { 'Accept': 'application/json' }
    })
      .then(function (res) { return res.ok ? fetch('/cart.js') : Promise.reject(res); })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        // The built site swaps the mark rather than hiding a "0": a gold pill
        // carrying the count when the bag has something in it, a small gold dot
        // when it does not. Toggling `hidden` alone never worked here — the
        // badge is `.grid`, and `display: grid` outranks the `[hidden]` rule,
        // so the zero stayed on screen.
        $$('[data-cart-count]').forEach(function (el) { el.textContent = cart.item_count; });
        $$('[data-cart-badge]').forEach(function (el) { el.hidden = cart.item_count === 0; });
        $$('[data-cart-dot]').forEach(function (el) { el.hidden = cart.item_count > 0; });
        renderCart(cart);
        // Say so. A silent success reads as a broken button.
        if (by) {
          var was = by.textContent;
          by.textContent = 'Added';
          window.setTimeout(function () { by.textContent = was; }, 1600);
        }
        var drawer = $('[aria-label="Your bag"]');
        if (drawer) drawer.classList.add('is-open');
        var scrim = $('[aria-hidden="true"].fixed') || $('[data-overlay-scrim]');
        if (scrim) scrim.classList.add('is-open');
      })
      .catch(function () { form.submit(); });

  /* The bag drawer is rendered from `cart` on page load, but an AJAX add does
     not reload the page — without this the drawer kept showing the markup it
     was built with, which is why adding a product still read "Your bag is
     empty". Rebuilds the same structure the Liquid emits. */
  function money(cents) {
    return 'R' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function renderCart(cart) {
    var body = $('[data-cart-body]');
    if (!body) return;

    if (!cart.item_count) {
      body.innerHTML =
        '<div class="flex flex-1 flex-col items-center justify-center gap-4 px-8 pb-16 text-center">' +
        '<p class="text-lg font-bold text-ink">Your bag is empty</p>' +
        '<p class="max-w-[22ch] text-sm text-slate-ink">Looks like you haven’t added anything yet.</p>' +
        '<a href="/collections/all?sort_by=created-descending" class="mt-2 inline-flex min-h-[3.25rem] items-center justify-center bg-ink px-8 py-3.5 type-spec text-[0.62rem] text-white transition-colors hover:bg-[#1d2f5e]">Shop New Arrivals</a>' +
        '</div>';
      return;
    }

    var rows = cart.items.map(function (item, i) {
      var variant = item.variant_title && item.variant_title.indexOf('Default') < 0
        ? '<p class="mt-0.5 text-xs text-slate-ink">' + esc(item.variant_title) + '</p>' : '';
      var img = item.image
        ? '<img src="' + esc(item.image) + '" alt="" class="h-20 w-16 shrink-0 bg-bone object-cover">'
        : '<span class="h-20 w-16 shrink-0 bg-bone"></span>';
      return '<li class="flex gap-4 border-b border-ink/10 pb-4">' + img +
        '<div class="min-w-0 flex-1">' +
        '<a href="' + esc(item.url) + '" class="block truncate text-sm font-bold text-ink hover:text-gold-deep">' + esc(item.product_title) + '</a>' +
        variant +
        '<p class="mt-1 text-xs text-slate-ink">Qty ' + item.quantity + '</p>' +
        '<a href="/cart/change?line=' + (i + 1) + '&quantity=0" class="mt-1 inline-block text-[0.68rem] uppercase tracking-[0.14em] text-slate-ink underline hover:text-gold-deep">Remove</a>' +
        '</div>' +
        '<p class="shrink-0 text-sm font-bold text-ink">' + money(item.final_line_price) + '</p>' +
        '</li>';
    }).join('');

    body.innerHTML =
      '<ul class="flex-1 space-y-4 overflow-y-auto px-6 py-5">' + rows + '</ul>' +
      '<div class="border-t border-ink/10 px-6 py-5">' +
      '<div class="flex items-center justify-between text-sm text-ink"><span>Subtotal</span>' +
      '<span class="font-bold" data-cart-subtotal>' + money(cart.total_price) + '</span></div>' +
      '<p class="mt-1 text-xs text-slate-ink">Shipping and taxes calculated at checkout.</p>' +
      '<a href="/checkout" class="mt-4 inline-flex min-h-[3.25rem] w-full items-center justify-center bg-ink px-8 py-3.5 type-spec text-[0.62rem] text-white transition-colors hover:bg-[#1d2f5e]">Checkout</a>' +
      '<a href="/cart" class="mt-2 inline-flex min-h-[2.75rem] w-full items-center justify-center border border-ink/20 px-8 py-2.5 type-spec text-[0.62rem] text-ink transition-colors hover:border-ink">View Bag</a>' +
      '</div>';
  }

  function esc(v) {
    return String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  });

  /* ── ScrollTrigger refresh ──────────────────────────────────────────────
     Each module measures its own trigger the moment it runs, but the modules
     run in order and a later one can change the page's height underneath an
     earlier one — the collection deck's pin reserves 1911px, which pushed the
     process journey ~2500px below the position its trigger had recorded, so
     that section never fired and its timeline sat paused. Images settling in
     move things by a smaller amount for the same reason. Re-measure once
     everything is on the page, and again after load. */
  (function refreshTriggers() {
    if (!window.ScrollTrigger) return;
    var refresh = function () { window.ScrollTrigger.refresh(); };
    window.setTimeout(refresh, 0);
    if (document.readyState === 'complete') window.setTimeout(refresh, 400);
    else window.addEventListener('load', function () { window.setTimeout(refresh, 400); });
  })();
})();

/* ── Collection filters ─────────────────────────────────────────────────────
   Filters and sorts a grid of product cards in place, against the data
   attributes each card carries. No round trip: these grids run to about a
   hundred products, so re-fetching per tick would be slower than the tap that
   asked for it, and this works identically on the partner pages, which have no
   collection object behind them.

   Cards are only ever hidden, never detached, so clearing a filter restores
   the original order without a re-render. */
(function collectionFilters() {
  var rail = document.querySelector('[data-filter-rail]');
  if (!rail) return;

  var grid = document.querySelector('[data-product-grid]');
  if (!grid) {
    var firstCard = document.querySelector('[data-product-filter]');
    grid = firstCard ? firstCard.parentElement : null;
  }
  if (!grid) return;

  var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-product-filter]'));
  if (!cards.length) return;

  var countEl = document.querySelector('[data-filter-count]');
  var emptyEl = document.querySelector('[data-filter-empty]');
  var sortEl = document.querySelector('[data-sort-by]');

  /* "New" is relative to the newest thing in the grid rather than a fixed
     date, so the facet keeps meaning something as the catalogue ages. */
  var newest = 0;
  cards.forEach(function (c) {
    var t = parseInt(c.getAttribute('data-created'), 10) || 0;
    if (t > newest) newest = t;
  });
  var NEW_WINDOW = 60 * 60 * 24 * 45;

  var order = new Map();
  cards.forEach(function (c, i) { order.set(c, i); });

  function selected(kind) {
    var out = [];
    rail.querySelectorAll('input[data-filter="' + kind + '"]:checked').forEach(function (i) {
      out.push(i.value);
    });
    rail.querySelectorAll('[data-filter="' + kind + '"][data-on="true"]').forEach(function (b) {
      out.push(b.getAttribute('data-value'));
    });
    return out;
  }

  function listOf(el, attr) {
    return (el.getAttribute(attr) || '').split(',').map(function (s) {
      return s.trim().toLowerCase();
    }).filter(Boolean);
  }

  function matches(card) {
    /* The rail now offers a single kind of control — a facet the card either
       carries or does not — because that is the whole filter model the approved
       site uses. Ticking two boxes is a union within and across groups, as it
       is there. The older kinds below are left in place for any grid still
       rendering them. */
    var facets = selected('facet');
    if (facets.length) {
      var mine = listOf(card, 'data-facets');
      var fhit = facets.some(function (f) { return mine.indexOf(f.toLowerCase()) !== -1; });
      if (!fhit) return false;
    }

    var types = selected('type');
    if (types.length && types.indexOf(card.getAttribute('data-type')) === -1) return false;

    /* Gender reads the derived attribute, not the raw tags: the store only
       tags a fifth of the catalogue, so matching tags alone returned an empty
       grid on Shop All and looked like a broken control. */
    var tags = selected('tag');
    if (tags.length) {
      var have = listOf(card, 'data-gender').concat(listOf(card, 'data-tags'));
      var hit = tags.some(function (t) { return have.indexOf(t.toLowerCase()) !== -1; });
      if (!hit) return false;
    }

    var colours = selected('colour');
    if (colours.length) {
      var cs = listOf(card, 'data-colours');
      var chit = colours.some(function (c) { return cs.indexOf(c.toLowerCase()) !== -1; });
      if (!chit) return false;
    }

    var bands = selected('price');
    if (bands.length) {
      var price = parseFloat(card.getAttribute('data-price')) || 0;
      var inBand = bands.some(function (b) {
        var parts = b.split('-');
        return price >= parseFloat(parts[0]) && price <= parseFloat(parts[1]);
      });
      if (!inBand) return false;
    }

    if (selected('sale').length && card.getAttribute('data-sale') !== 'true') return false;

    if (selected('new').length) {
      var made = parseInt(card.getAttribute('data-created'), 10) || 0;
      if (newest - made > NEW_WINDOW) return false;
    }
    return true;
  }

  function apply() {
    var shown = 0;
    cards.forEach(function (c) {
      var ok = matches(c);
      c.hidden = !ok;
      c.style.display = ok ? '' : 'none';
      if (ok) shown++;
    });
    if (countEl) countEl.textContent = shown === cards.length
      ? cards.length + ' products'
      : shown + ' of ' + cards.length + ' products';
    if (emptyEl) emptyEl.hidden = shown !== 0;
    sortNow();
  }

  function sortNow() {
    if (!sortEl) return;
    var mode = sortEl.value;
    var live = cards.filter(function (c) { return !c.hidden; });
    live.sort(function (a, b) {
      if (mode === 'price-asc') return (+a.getAttribute('data-price')) - (+b.getAttribute('data-price'));
      if (mode === 'price-desc') return (+b.getAttribute('data-price')) - (+a.getAttribute('data-price'));
      if (mode === 'newest') return (+b.getAttribute('data-created')) - (+a.getAttribute('data-created'));
      if (mode === 'title') return (a.getAttribute('data-title') || '').localeCompare(b.getAttribute('data-title') || '');
      return order.get(a) - order.get(b);
    });
    live.forEach(function (c) { grid.appendChild(c); });
  }

  rail.addEventListener('change', function (e) {
    if (e.target && e.target.matches('input[data-filter]')) apply();
  });

  rail.addEventListener('click', function (e) {
    /* One facet at a time. The built site filters to a single choice rather
       than accumulating ticks, so selecting one clears the rest; clicking the
       active one again returns to the unfiltered grid. */
    var facet = e.target.closest && e.target.closest('button[data-filter="facet"]');
    if (facet) {
      var was = facet.getAttribute('data-on') === 'true';
      rail.querySelectorAll('button[data-filter="facet"]').forEach(function (b) {
        b.setAttribute('data-on', 'false');
      });
      facet.setAttribute('data-on', was ? 'false' : 'true');
      apply();
      return;
    }

    var chip = e.target.closest && e.target.closest('button[data-filter="colour"]');
    if (chip) {
      chip.setAttribute('data-on', chip.getAttribute('data-on') === 'true' ? 'false' : 'true');
      apply();
      return;
    }
    if (e.target.closest && e.target.closest('[data-filter-clear]')) {
      rail.querySelectorAll('input[data-filter]').forEach(function (i) { i.checked = false; });
      rail.querySelectorAll('[data-filter="colour"], button[data-filter="facet"]').forEach(function (b) { b.setAttribute('data-on', 'false'); });
      apply();
      return;
    }
    var toggle = e.target.closest && e.target.closest('[data-filter-toggle]');
    if (toggle) {
      var body = rail.querySelector('[data-filter-body]');
      if (body) body.classList.toggle('hidden');
      return;
    }


    /* Each pill opens its own group. The options stay in the DOM when closed so
       an active filter keeps applying even while its group is collapsed. */
    var group = e.target.closest && e.target.closest('[data-group-toggle]');
    if (group) {
      var opts = group.parentElement.querySelector('.filter-options');
      if (opts) {
        var open = group.getAttribute('aria-expanded') === 'true';
        group.setAttribute('aria-expanded', open ? 'false' : 'true');
        opts.hidden = open;
      }
    }
  });


  /* The swatch -> photograph matcher that stood here now lives in
     nyeleti-fixes.js. It matched on straight RGB distance, which cannot tell a
     very dark green from black, and it wrote the same data-colour-src attribute
     as the replacement — so whichever finished last decided the pairing. The
     version in nyeleti-fixes.js compares hue instead and drives the switch
     through the gallery's own thumbnail. */
  (function swatchImagesRemoved() { return;
    var root = document.querySelector('[data-product-root]');
    if (!root) return;
    var swatches = root.querySelectorAll('[data-swatch]');
    var thumbs = root.querySelectorAll('[data-product-thumb]');
    if (swatches.length < 2 || thumbs.length < 2) return;

    function dominant(img) {
      try {
        var c = document.createElement('canvas');
        var W = 40, H = 40;
        c.width = W; c.height = H;
        var cx = c.getContext('2d', { willReadFrequently: true });
        cx.drawImage(img, 0, 0, W, H);
        var d = cx.getImageData(0, 0, W, H).data;
        var corner = [d[0], d[1], d[2]];
        var acc = [0, 0, 0], n = 0;
        for (var y = H * 0.2; y < H * 0.85; y++) {
          for (var x = W * 0.2; x < W * 0.8; x++) {
            var o = ((y | 0) * W + (x | 0)) * 4;
            var r = d[o], g = d[o + 1], b = d[o + 2];
            // Skip the backdrop and blown highlights.
            if (Math.abs(r - corner[0]) + Math.abs(g - corner[1]) +
                Math.abs(b - corner[2]) < 60) continue;
            if (r > 242 && g > 242 && b > 242) continue;
            acc[0] += r; acc[1] += g; acc[2] += b; n++;
          }
        }
        if (!n) return null;
        return [acc[0] / n, acc[1] / n, acc[2] / n];
      } catch (e) { return null; }
    }

    function rgbOf(el) {
      var m = getComputedStyle(el).backgroundColor.match(/(\d+), (\d+), (\d+)/);
      return m ? [+m[1], +m[2], +m[3]] : null;
    }

    /* The thumbnails on the page are ordinary <img> tags without a CORS
       attribute, so drawing one to a canvas taints it and reading pixels back
       throws. Each photograph is therefore reloaded as an anonymous-origin
       image — Shopify's CDN allows it — purely to sample its colour. */
    function load(src) {
      return new Promise(function (res) {
        var im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = function () { res(im); };
        im.onerror = function () { res(null); };
        im.src = src.split('?')[0] + '?width=200';
      });
    }

    async function pair() {
      var shots = [];
      for (var i = 0; i < thumbs.length; i++) {
        var src = thumbs[i].getAttribute('data-src');
        if (!src) continue;
        var im = await load(src);
        if (!im) continue;
        var dom = dominant(im);
        if (dom) shots.push({ src: src, dom: dom });
      }
      if (shots.length < 2) return;

      swatches.forEach(function (sw) {
        var want = rgbOf(sw);
        if (!want) return;
        var best = null, bestD = Infinity;
        shots.forEach(function (s) {
          var d = Math.abs(s.dom[0] - want[0]) + Math.abs(s.dom[1] - want[1]) +
                  Math.abs(s.dom[2] - want[2]);
          if (d < bestD) { bestD = d; best = s; }
        });
        // Only claim a match when the photograph is genuinely that colour.
        if (best && bestD < 190) sw.setAttribute('data-colour-src', best.src);
      });

      root.addEventListener('click', function (e) {
        var sw = e.target.closest && e.target.closest('[data-swatch]');
        if (!sw) return;
        var src = sw.getAttribute('data-colour-src');
        if (!src) return;
        var main = root.querySelector('[data-product-main]');
        if (!main) return;
        main.removeAttribute('srcset');
        main.src = src;
      }, true);
    }

    if (document.readyState === 'complete') pair();
    else window.addEventListener('load', pair);
  })();

  /* Product card images: fit chosen per photograph, not per stylesheet.

     One rule cannot serve this catalogue. Most shots are cut-outs on their own
     white ground, roughly portrait — those have to be *contained*, or the
     garment loses its hems to the 3/4 box. But a handful are landscape group
     photographs, and containing one of those leaves a thin strip marooned in a
     tall card, which reads just as broken.

     So the decision is made from the image's own proportions once it has
     loaded: markedly wider than the card gets `cover` and fills it, everything
     else stays `contain`. The card is white either way, so a contained cut-out
     sits on a continuous surface rather than inside a second frame. */
  (function cardFit() {
    var SEL = '[data-product-image] img, [data-product-grid] img,' +
              '[data-product-card] img, .product-card__photos img,' +
              // The product page's main frame and its thumbnail strip follow
              // the same rule: a photograph fills its frame, a cut-out is shown
              // whole on white. Leaving these on a flat `contain` put a band of
              // dead space around every landscape shot.
              'img[data-product-main], [data-product-thumb] img';

    /* The signal is the photograph's background, not its shape.

       A cut-out sits on white with margin already built in: contain it and it
       lands cleanly on the white card. A full-bleed photograph — model on a
       dark set, or shot on location — has to cover, whichever way round it is,
       or it floats in the card with bands down the sides or across the top.
       Shape alone cannot tell them apart: both come in portrait.

       So the four corners are sampled. All four near-white means a cut-out. */
    var cache = {};

    function isCutout(img) {
      var key = (img.currentSrc || img.src).split('?')[0];
      if (key in cache) return cache[key];
      try {
        var c = document.createElement('canvas');
        c.width = c.height = 12;
        var cx = c.getContext('2d', { willReadFrequently: true });
        cx.drawImage(img, 0, 0, 12, 12);
        var d = cx.getImageData(0, 0, 12, 12).data;
        var pts = [[1, 1], [10, 1], [1, 10], [10, 10]];
        var white = 0;
        for (var i = 0; i < pts.length; i++) {
          var o = (pts[i][1] * 12 + pts[i][0]) * 4;
          if (d[o] > 232 && d[o + 1] > 232 && d[o + 2] > 232) white++;
        }
        cache[key] = white >= 4;
      } catch (e) {
        cache[key] = false;   // tainted or not decodable: treat as photograph
      }
      return cache[key];
    }

    /* Fit by shape, not by subject.

       This used to ask whether the photograph was a cut-out on white: cut-outs
       got `contain`, everything else `cover`. That produced both of the faults
       on the shop grid at once — a square render floated in a band of dead
       space, and the hover frame, being a photograph, was cropped.

       The shape of the picture is what actually matters. `cover` is used only
       when filling the frame costs less than a twentieth of the picture, which
       covers the 1200x1600 renders that match the 3/4 box exactly. Anything a
       different shape keeps its whole subject, and the card ground is the same
       white the shots are taken on, so what is left does not read as a band. */
    /* A photograph is framed to fill, so cropping it is ordinary framing and
       0.30 is what a 3/4 card costs a 2:3 portrait plus room to spare. A
       landscape group shot is 50% off and stays whole — filling with one of
       those takes the people at the edges out of the picture. */
    var CROP_ALLOWANCE = 0.30;

    /* Landscape photographs fill the card. A 3:2 frame in a 3/4 box is half its
       width gone, so 0.30 left every one of them letterboxed with dead space
       above and below the picture. Filling costs the outer edges of a wide shot,
       which is the trade asked for. Studio renders are unaffected: they keep the
       ceiling measured off their own plain ground, so a garment is still never
       cut. */
    var LANDSCAPE_ALLOWANCE = 0.55;

    /* Studio renders are the exception, and guessing at them by shape is what
       cut the shorts. They sit on a swept white ground with the garment nearly
       edge to edge, so a near-square render in a 3/4 card loses 27% off the
       sides — which is the hems and the logo, not the ground. Every render was
       measured offline for how much plain ground it actually carries; that map
       is the ceiling. Crop into the ground, never past it. Anything not in the
       map is a photograph and takes the rule above. */
    function groundEntry(img) {
      var map = window.NYELETI_GROUND;
      if (!map) return null;
      var src = img.currentSrc || img.src || '';
      return map[src.split('?')[0].split('/').pop()] || null;
    }

    /* Several shoots were swept on light grey rather than white. Letterboxed
       against a white frame those read as a grey slab floating inside the card,
       which is what the cheerleader set showed. Paint the frame in the picture's
       own backdrop instead and the join disappears, whichever colour it is.
       Pictures with no single backdrop carry no colour and are left alone. */
    function paintGround(img, entry) {
      var box = img.parentElement;
      while (box && box.clientHeight === 0) box = box.parentElement;
      if (!box) return;
      if (entry && entry[2]) box.style.setProperty('background-color', entry[2], 'important');
      else box.style.removeProperty('background-color');
    }

    // Returns true only when the picture was measured and a fit applied, so a
    // card that could not be measured is retried rather than written off.
    function fit(img) {
      if (!img.naturalWidth || !img.naturalHeight) return false;
      var box = img.getBoundingClientRect();
      if (!box.width || !box.height) return false;
      var frame = box.width / box.height;
      var shot = img.naturalWidth / img.naturalHeight;
      var wide = shot > frame;
      var loss = wide ? 1 - frame / shot : 1 - shot / frame;

      var entry = groundEntry(img);
      /* Upright photographs are never cropped -- the measured ground was taken
         at one frame ratio and cards are not always that ratio. See
         nyeleti-fixes.js. */
      var ceiling = (entry && wide) ? entry[0] : null;
      var allowed;
      if (ceiling === null || ceiling === undefined) {
        /* No measured ground for this file. The map is keyed by filename, so
           every photograph swapped in since it was built falls here, and the
           0.30 allowance was cropping all of them. Show an unmeasured upright
           photograph whole; only landscape frames still fill. */
        allowed = shot > 1.02 ? LANDSCAPE_ALLOWANCE : 0;
      } else {
        allowed = ceiling;
      }
      /* Nothing is cropped, at any width or shape -- see nyeleti-fixes.js.
         Per-image cropping rules cut heads off upright shots and zoomed
         near-square renders into close-ups. */
      /* Fill when the trim is cheap, whichever way the picture misses -- a
         direction test letterboxed anything wider by a hair and left a dead
         band. See nyeleti-fixes.js. */
      var cover = loss <= 0.15;

      img.style.setProperty('object-fit', cover ? 'cover' : 'contain', 'important');
      /* Only a letterboxed picture shows the frame behind it. */
      paintGround(img, cover ? null : entry);
      return true;
    }

    function sweep() {
      document.querySelectorAll(SEL).forEach(function (img) {
        // Each image is decided once. Without this the observer below re-ran
        // the canvas read for every card on every DOM change, which pinned the
        // main thread and made the page feel unresponsive to clicks.
        if (img.dataset.fitDone) return;
        // Marked done only when the picture was actually measured. Marking it
        // done on `complete` alone wrote off every card whose frame had no
        // height yet -- the "You might also like" rail, which is built after
        // load -- and it was never looked at again. See nyeleti-fixes.js.
        if (img.complete) {
          if (fit(img)) img.dataset.fitDone = '1';
        } else {
          img.addEventListener('load', function () {
            if (fit(img)) img.dataset.fitDone = '1';
          });
        }
      });
    }

    var queued = false;
    function schedule() {
      if (queued) return;
      queued = true;
      // Coalesce bursts of mutations into one pass on the next idle frame.
      (window.requestAnimationFrame || setTimeout)(function () {
        queued = false;
        sweep();
      });
    }

    sweep();
    window.addEventListener('load', sweep);
    // Cards arrive from lazy sections and from filtering, so keep watching —
    // but only for nodes being added, and only one pass per frame.
    if (window.MutationObserver) {
      new MutationObserver(function (recs) {
        for (var i = 0; i < recs.length; i++) {
          if (recs[i].addedNodes && recs[i].addedNodes.length) return schedule();
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  })();

  /* The Hide/Show Filters control sits in the toolbar above the grid, outside
     the rail, so it needs wiring of its own — and the grid reclaims the column
     while the rail is away. */
  (function railToggle() {
    var btn = document.querySelector('[data-filter-toggle]');
    var layout = document.querySelector('.filter-layout');
    if (!btn || !layout || !rail) return;

    var label = btn.querySelector('[data-filter-toggle-label]');
    function paint(open) {
      if (open) { rail.removeAttribute('hidden'); } else { rail.setAttribute('hidden', ''); }
      layout.classList.toggle('is-unfiltered', !open);
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (label) label.textContent = open ? 'Hide Filters' : 'Show Filters';
    }

    /* On a phone the rail is a full-height stack of facets sitting directly
       above the grid, so leaving it open means scrolling past every group to
       reach the first product. It starts collapsed there and the toolbar button
       — which is the only filter control on the page — opens it. Desktop has a
       side column for the rail and keeps it open, as the approved site does. */
    paint(window.matchMedia('(min-width: 768px)').matches);

    btn.addEventListener('click', function () {
      paint(rail.hasAttribute('hidden'));
    });
  })();

  (function standaloneRailToggle() {
    // Partner pages carry no collection toolbar, so the rail renders its own
    // toggle. Without it the whole tree sits open above the products on a
    // phone and the customer has to scroll past every facet to reach the grid.
    var btn = document.querySelector('[data-rail-toggle]');
    if (!btn || !rail) return;
    var body = rail.querySelector('[data-filter-body]');
    if (!body) return;
    btn.addEventListener('click', function () {
      var open = !body.classList.contains('hidden');
      body.classList.toggle('hidden', open);
      var label = btn.querySelector('[data-rail-toggle-label]');
      if (label) label.textContent = open ? 'Show Filters' : 'Hide Filters';
    });
  })();

  if (sortEl) sortEl.addEventListener('change', sortNow);

  apply();
})();